
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FontAwesomeIcon,
} from "@fortawesome/react-fontawesome";

import {
  faBoxesStacked,
  faFilter,
  faRotateLeft,
  faSearch,
  faWarehouse,
} from "@fortawesome/free-solid-svg-icons";

import {
  toast,
} from "react-hot-toast";

import {
  Box,
  Modal,
} from "@mui/material";

import {
  inventoryApi,
} from "../api/inventoryApi";

import {
  warehousesApi,
} from "../api/warehousesApi";

import {
  productsApi,
} from "../api/productsApi";

import {
  useAuth,
} from "../contexts/AuthContext";

import {
  ApiError,
} from "../api/apiError";

import type {
  AdminProduct,
  InventoryReservation,
  InventoryReservationStatus,
  InventoryTransaction,
  InventoryTransactionSource,
  InventoryTransactionType,
} from "../types/products.types";


import type {
  WarehouseRecord,
  WarehouseStockRecord,
} from "../types/warehouses.types";


import "../screenstyles/Inventory.css";


const PAGE_SIZE = 30;


/* =========================================================
 * Helpers
 * ======================================================= */

function getInventoryErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  if (
    !(error instanceof ApiError)
  ) {
    return fallbackMessage;
  }

  switch (error.code) {
    case "FORBIDDEN":
      return "لا تملك الصلاحية المطلوبة لتنفيذ هذه العملية.";

    case "UNAUTHORIZED":
      return "انتهت جلسة تسجيل الدخول.";

    case "VALIDATION_ERROR":
      return "البيانات المدخلة غير صحيحة.";

    case "INVENTORY_RESERVATION_NOT_FOUND":
      return "حجز المخزون غير موجود.";

    case "INVENTORY_RESERVATION_NOT_ACTIVE":
      return "هذا الحجز غير نشط ولا يمكن تحريره.";

    case "PRODUCT_NOT_FOUND":
      return "المنتج المرتبط بالحجز غير موجود.";

      case "WAREHOUSE_INVENTORY_NOT_ENABLED":
  return "نظام المخزون حسب المستودعات لم يتم تفعيله بعد. أكمل Migration ثم فعّل Warehouse Inventory.";

case "WAREHOUSE_NOT_FOUND":
  return "المستودع المحدد غير موجود.";

case "WAREHOUSE_INACTIVE":
  return "المستودع المحدد غير فعال.";

case "WAREHOUSE_STOCK_NOT_FOUND":
  return "لا يوجد سجل مخزون لهذا المنتج داخل المستودع المحدد.";

case "WAREHOUSE_ID_REQUIRED":
  return "اختر المستودع قبل تنفيذ تعديل المخزون.";

case "STOCK_QUANTITY_NEGATIVE":
  return "لا يمكن أن تصبح كمية المخزون سالبة.";

case "INSUFFICIENT_PRODUCT_STOCK":
  return "الكمية المتاحة للمنتج غير كافية.";

case "WAREHOUSE_STOCK_INSUFFICIENT":
case "INSUFFICIENT_WAREHOUSE_STOCK":
  return "الكمية المتاحة في المستودع غير كافية.";

case "INVENTORY_RESERVATION_WAREHOUSE_REQUIRED":
  return "هذا الحجز غير مرتبط بمستودع.";

case "INVENTORY_RESERVATION_ORDER_REQUIRED":
  return "إعادة توزيع الحجز متاحة فقط للحجوزات المرتبطة بطلب.";

case "INVENTORY_RESERVATION_REALLOCATION_SAME_WAREHOUSE":
  return "لا يمكن نقل الحجز إلى نفس المستودع.";

case "INVENTORY_RESERVATION_REALLOCATION_QUANTITY_EXCEEDS_SOURCE":
  return "الكمية المطلوب نقلها أكبر من كمية الحجز الحالية.";

case "INVENTORY_RESERVATION_REALLOCATION_LEGACY_UNSUPPORTED":
  return "هذا حجز قديم تم إنشاؤه قبل نظام المستودعات ولا يمكن إعادة توزيعه.";

case "INVENTORY_RESERVATION_REALLOCATION_IDEMPOTENCY_CONFLICT":
case "IDEMPOTENCY_KEY_CONFLICT":
  return "حدث تعارض في مفتاح العملية. أعد فتح الحجز وحاول مرة أخرى.";

    default:
      return (
        error.message ||
        fallbackMessage
      );
  }
}


function getTransactionTypeLabel(
  type:
    InventoryTransactionType,
): string {
  switch (type) {
    case "set":
      return "تعيين كمية";

    case "adjustment":
      return "تعديل مخزون";

    case "reservation":
      return "حجز مخزون";

    case "reservation_release":
      return "تحرير حجز";

      case "reservation_consumed":
  return "استهلاك حجز";

case "reservation_reallocation_out":
  return "نقل حجز من مستودع";

case "reservation_reallocation_in":
  return "استلام حجز في مستودع";


    case "order_deduction":
      return "خصم طلب";

    case "order_release":
      return "إرجاع طلب";

    case "import":
      return "استيراد";

    case "restore":
      return "استعادة";

    case "bulk_adjustment":
      return "تعديل جماعي";

      case "warehouse_transfer_out":
  return "نقل مخزون - صادر";

case "warehouse_transfer_in":
  return "نقل مخزون - وارد";

    default:
      return type;
  }
}

function getDisplayedTransactionQuantities(
  transaction: InventoryTransaction,
): {
  previousQuantity: number;
  delta: number;
  newQuantity: number;
} {
  const hasWarehouseQuantities =
    transaction.warehousePreviousQuantity !== undefined &&
    transaction.warehouseNewQuantity !== undefined;

  /*
   * Warehouse adjustment.
   *
   * The generic previous/new quantities represent
   * the GLOBAL product stock.
   *
   * For a warehouse-linked adjustment we want the
   * audit table to show the quantity that actually
   * changed inside that warehouse.
   */
  if (
    transaction.type === "adjustment" &&
    hasWarehouseQuantities
  ) {
    return {
      previousQuantity:
        transaction.warehousePreviousQuantity as number,

      delta:
        (transaction.warehouseNewQuantity as number) -
        (transaction.warehousePreviousQuantity as number),

      newQuantity:
        transaction.warehouseNewQuantity as number,
    };
  }


  // reservation_consumed:
// AVAILABLE does not change.
// RESERVED decreases.
// PHYSICAL ON-HAND decreases.
//
// Therefore, display the warehouse physical stock movement
// instead of the unchanged global available quantity.
  if (
  transaction.type === "reservation_consumed" &&
  typeof transaction.warehousePreviousOnHandQuantity === "number" &&
  typeof transaction.warehouseNewOnHandQuantity === "number"
) {
  return {
    previousQuantity: transaction.warehousePreviousOnHandQuantity,
    delta:
      transaction.warehouseNewOnHandQuantity -
      transaction.warehousePreviousOnHandQuantity,
    newQuantity: transaction.warehouseNewOnHandQuantity,
  };
}

  /*
   * Warehouse reservation.
   *
   * Reservation moves stock:
   *
   * AVAILABLE -> RESERVED
   *
   * Example:
   *
   * available before = 70
   * reserved quantity = 5
   * available after  = 65
   *
   * Physical on-hand stock does not change.
   *
   * The backend stores the warehouse-specific
   * AVAILABLE before/after values separately.
   */
if (
  (
    transaction.type === "reservation" ||
    transaction.type === "reservation_release" ||
    transaction.type === "reservation_reallocation_out" ||
    transaction.type === "reservation_reallocation_in"
  ) &&
  hasWarehouseQuantities
) {
  const warehousePreviousQuantity =
    transaction.warehousePreviousQuantity as number;

  const warehouseNewQuantity =
    transaction.warehouseNewQuantity as number;

  return {
    previousQuantity:
      warehousePreviousQuantity,

    delta:
      warehouseNewQuantity -
      warehousePreviousQuantity,

    newQuantity:
      warehouseNewQuantity,
  };
}

  /*
   * Warehouse-to-warehouse stock transfer.
   *
   * Global product quantity does not change,
   * therefore use the warehouse-specific snapshot.
   */
  const isWarehouseTransfer =
    transaction.type === "warehouse_transfer_out" ||
    transaction.type === "warehouse_transfer_in";

  if (
    isWarehouseTransfer &&
    hasWarehouseQuantities &&
    transaction.transferQuantity !== undefined
  ) {
    return {
      previousQuantity:
        transaction.warehousePreviousQuantity as number,

      delta:
        transaction.type === "warehouse_transfer_out"
          ? -transaction.transferQuantity
          : transaction.transferQuantity,

      newQuantity:
        transaction.warehouseNewQuantity as number,
    };
  }

  /*
   * All other inventory transactions.
   *
   * Fall back to the generic/global quantities.
   */
  return {
    previousQuantity:
      transaction.previousQuantity,

    delta:
      transaction.delta,

    newQuantity:
      transaction.newQuantity,
  };
}


function getTransactionBalanceTypeLabel(
  transaction: InventoryTransaction,
): string {
  if (
    transaction.type ===
    "reservation_consumed"
  ) {
    return "الرصيد الفعلي";
  }

  if (
    transaction.warehouseId &&
    (
      transaction.type ===
        "adjustment" ||
      transaction.type ===
        "reservation" ||
      transaction.type ===
        "reservation_release" ||
      transaction.type ===
        "reservation_reallocation_out" ||
      transaction.type ===
        "reservation_reallocation_in" ||
      transaction.type ===
        "warehouse_transfer_out" ||
      transaction.type ===
        "warehouse_transfer_in"
    )
  ) {
    return "الرصيد المتاح";
  }

  return "الرصيد العام";
}


function getTransactionSourceLabel(
  source:
    InventoryTransactionSource,
): string {
  switch (source) {
    case "dashboard":
      return "لوحة التحكم";

    case "api":
      return "API";

    case "order":
      return "طلب";

    case "import":
      return "استيراد";

    case "bulk":
      return "عملية جماعية";

    case "system":
      return "النظام";

    default:
      return source;
  }
}


function getReservationStatusLabel(
  status:
    InventoryReservationStatus,
): string {
  switch (status) {
    case "active":
      return "نشط";

      case "removed":
  return "تمت إزالته";

    case "released":
      return "محرر";

    case "consumed":
      return "مستهلك";

    case "cancelled":
      return "ملغي";

    default:
      return status;
  }
}


function formatInventoryDate(
  value: unknown,
): string {
  if (
    !value ||
    typeof value !== "object"
  ) {
    return "-";
  }

  const timestamp =
    value as {
      _seconds?: number;
      seconds?: number;
    };

  const seconds =
    timestamp.seconds ??
    timestamp._seconds;

  if (
    seconds === undefined
  ) {
    return "-";
  }

  return new Date(
    seconds * 1000,
  ).toLocaleString(
    "ar-IQ",
  );
}

function createReallocationIdempotencyKey(): string {
  return [
    "dashboard-reallocation",
    Date.now(),
    Math.random()
      .toString(36)
      .slice(2, 12),
  ].join("-");
}


/* =========================================================
 * Component
 * ======================================================= */

const Inventory: React.FC =
  () => {

    const {
  hasPermission,
} = useAuth();


const canViewWarehouses =
  hasPermission(
    "warehouses.view",
  );


const canReallocate =
  hasPermission(
    "inventory.reallocate",
  );


const canReserveInventory =
  hasPermission(
    "inventory.reserve",
  );


const canAdjustInventory =
  hasPermission(
    "inventory.adjust",
  );

    /* =====================================================
     * Active Tab
     * =================================================== */

    const [
      activeInventoryTab,
      setActiveInventoryTab,
    ] = useState<
  "transactions" |
  "reservations" |
  "warehouse-stock"
>(
      "transactions",
    );


    /* =====================================================
     * Product Search
     * =================================================== */

    const [
      productSearch,
      setProductSearch,
    ] = useState("");

    const [
      productSearchResults,
      setProductSearchResults,
    ] = useState<
      AdminProduct[]
    >([]);

    const [
      productSearchLoading,
      setProductSearchLoading,
    ] = useState(false);

    const [
      selectedProduct,
      setSelectedProduct,
    ] = useState<
      AdminProduct | null
    >(null);


    /* =====================================================
     * Common Filters
     * =================================================== */


    const [
  warehouses,
  setWarehouses,
] = useState<
  WarehouseRecord[]
>([]);


const [
  warehousesLoading,
  setWarehousesLoading,
] = useState(false);


const [
  warehouseId,
  setWarehouseId,
] = useState("");

    const [
      productId,
      setProductId,
    ] = useState("");

    const [
      orderId,
      setOrderId,
    ] = useState("");

    const [
      dateFrom,
      setDateFrom,
    ] = useState("");

    const [
      dateTo,
      setDateTo,
    ] = useState("");


    /* =====================================================
     * Transaction Filters
     * =================================================== */

    const [
      type,
      setType,
    ] = useState<
      InventoryTransactionType | ""
    >("");

    const [
      source,
      setSource,
    ] = useState<
      InventoryTransactionSource | ""
    >("");

    const [
      reservationId,
      setReservationId,
    ] = useState("");

    const [
      performedBy,
      setPerformedBy,
    ] = useState("");


    /* =====================================================
     * Reservation Filters
     * =================================================== */

    const [
      reservationStatus,
      setReservationStatus,
    ] = useState<
      InventoryReservationStatus | ""
    >("");


    /* =====================================================
     * Transactions State
     * =================================================== */

    const [
      transactions,
      setTransactions,
    ] = useState<
      InventoryTransaction[]
    >([]);

    const [
      loading,
      setLoading,
    ] = useState(true);

    const [
      loadingMore,
      setLoadingMore,
    ] = useState(false);

    const [
      hasMore,
      setHasMore,
    ] = useState(false);

    const [
      nextCursor,
      setNextCursor,
    ] = useState<
      string | null
    >(null);


    /* =====================================================
     * Reservations State
     * =================================================== */

    const [
      reservations,
      setReservations,
    ] = useState<
      InventoryReservation[]
    >([]);

    const [
      reservationsLoading,
      setReservationsLoading,
    ] = useState(false);

    const [
      reservationsLoadingMore,
      setReservationsLoadingMore,
    ] = useState(false);

    const [
      reservationsHasMore,
      setReservationsHasMore,
    ] = useState(false);

    const [
      reservationsNextCursor,
      setReservationsNextCursor,
    ] = useState<
      string | null
    >(null);


    /* =====================================================
 * Warehouse Stock State
 * =================================================== */

const [
  warehouseStocks,
  setWarehouseStocks,
] = useState<
  WarehouseStockRecord[]
>([]);


const [
  warehouseStocksLoading,
  setWarehouseStocksLoading,
] = useState(false);


const [
  warehouseStocksLoadingMore,
  setWarehouseStocksLoadingMore,
] = useState(false);


const [
  warehouseStocksHasMore,
  setWarehouseStocksHasMore,
] = useState(false);


const [
  warehouseStocksNextCursor,
  setWarehouseStocksNextCursor,
] = useState<
  string | null
>(null);


const [
  warehouseStockAvailability,
  setWarehouseStockAvailability,
] = useState<
  "all" |
  "in-stock" |
  "out-of-stock"
>("all");

    /* =====================================================
     * Transaction Details Modal
     * =================================================== */

    const [
      selectedTransaction,
      setSelectedTransaction,
    ] = useState<
      InventoryTransaction | null
    >(null);

    const [
      openTransactionModal,
      setOpenTransactionModal,
    ] = useState(false);


    /* =====================================================
     * Reservation Details Modal
     * =================================================== */

    const [
      selectedReservation,
      setSelectedReservation,
    ] = useState<
      InventoryReservation | null
    >(null);

    const [
      openReservationModal,
      setOpenReservationModal,
    ] = useState(false);

    const [
      releaseReason,
      setReleaseReason,
    ] = useState("");

    const [
      releasingReservation,
      setReleasingReservation,
    ] = useState(false);


    const [
  reallocatingReservation,
  setReallocatingReservation,
] = useState(false);


const [
  targetWarehouseId,
  setTargetWarehouseId,
] = useState("");


const [
  reallocationQuantity,
  setReallocationQuantity,
] = useState("");


const [
  reallocationReason,
  setReallocationReason,
] = useState("");


const [
  reallocationIdempotencyKey,
  setReallocationIdempotencyKey,
] = useState(
  createReallocationIdempotencyKey(),
);


/* =====================================================
 * Create Reservation State
 * =================================================== */

const [
  openCreateReservationModal,
  setOpenCreateReservationModal,
] = useState(false);

const [
  reservationWarehouseId,
  setReservationWarehouseId,
] = useState("");

const [
  reservationQuantity,
  setReservationQuantity,
] = useState("");

const [
  reservationReason,
  setReservationReason,
] = useState("");

const [
  reservationExpiresAt,
  setReservationExpiresAt,
] = useState("");

const [
  reservationCreating,
  setReservationCreating,
] = useState(false);

const [
  reservationWarehouseStock,
  setReservationWarehouseStock,
] = useState<WarehouseStockRecord | null>(null);

const [
  reservationWarehouseStockLoading,
  setReservationWarehouseStockLoading,
] = useState(false);

const [
  reservationIdempotencyKey,
  setReservationIdempotencyKey,
] = useState(
  [
    "dashboard-reservation",
    Date.now(),
    Math.random()
      .toString(36)
      .slice(2, 12),
  ].join("-"),
);


    /* =====================================================
     * Manual Adjustment State
     * =================================================== */

const [
  openAdjustmentModal,
  setOpenAdjustmentModal,
] = useState(false);


const [
  adjustmentWarehouseId,
  setAdjustmentWarehouseId,
] = useState("");





const [
  adjustmentQuantity,
  setAdjustmentQuantity,
] = useState("");


const [
  adjustmentReason,
  setAdjustmentReason,
] = useState("");


const [
  adjustmentOrderId,
  setAdjustmentOrderId,
] = useState("");


const [
  adjustmentReferenceId,
  setAdjustmentReferenceId,
] = useState("");


const [
  adjustmentProcessing,
  setAdjustmentProcessing,
] = useState(false);


/* =====================================================
 * Physical Stock Count Reconciliation State
 * =================================================== */

const [
  adjustmentWarehouseStock,
  setAdjustmentWarehouseStock,
] = useState<WarehouseStockRecord | null>(null);

const [
  adjustmentWarehouseStockLoading,
  setAdjustmentWarehouseStockLoading,
] = useState(false);


/* =====================================================
 * Separate Manual Adjustment State
 * =================================================== */

type ManualAdjustmentMode =
  | "increase"
  | "decrease";

const [
  openManualAdjustmentModal,
  setOpenManualAdjustmentModal,
] = useState(false);

const [
  manualAdjustmentWarehouseId,
  setManualAdjustmentWarehouseId,
] = useState("");

const [
  manualAdjustmentMode,
  setManualAdjustmentMode,
] = useState<ManualAdjustmentMode>("increase");

const [
  manualAdjustmentQuantity,
  setManualAdjustmentQuantity,
] = useState("");

const [
  manualAdjustmentReason,
  setManualAdjustmentReason,
] = useState("");

const [
  manualAdjustmentOrderId,
  setManualAdjustmentOrderId,
] = useState("");

const [
  manualAdjustmentReferenceId,
  setManualAdjustmentReferenceId,
] = useState("");

const [
  manualAdjustmentProcessing,
  setManualAdjustmentProcessing,
] = useState(false);

const [
  manualAdjustmentWarehouseStock,
  setManualAdjustmentWarehouseStock,
] = useState<WarehouseStockRecord | null>(null);

const [
  manualAdjustmentWarehouseStockLoading,
  setManualAdjustmentWarehouseStockLoading,
] = useState(false);

    /* =====================================================
     * Product Search
     * =================================================== */

    useEffect(
      () => {
        const searchValue =
          productSearch.trim();

        if (
          searchValue.length < 2 ||
          selectedProduct
        ) {
          setProductSearchResults(
            [],
          );

          return;
        }

        const timeout =
          window.setTimeout(
            async () => {
              try {
                setProductSearchLoading(
                  true,
                );

                const result =
                  await productsApi.adminList(
                    {
                      search:
                        searchValue,

                      limit:
                        10,
                    },
                  );

                setProductSearchResults(
                  result.items,
                );
              } catch (error) {
                console.error(
                  "Product search failed:",
                  error,
                );

                setProductSearchResults(
                  [],
                );
              } finally {
                setProductSearchLoading(
                  false,
                );
              }
            },
            350,
          );

        return () => {
          window.clearTimeout(
            timeout,
          );
        };
      },
      [
        productSearch,
        selectedProduct,
      ],
    );

    /* =====================================================
 * Warehouses
 * =================================================== */

const loadWarehouses =
  useCallback(
    async () => {
      if (
        !canViewWarehouses
      ) {
        setWarehouses(
          [],
        );

        return;
      }

      try {
        setWarehousesLoading(
          true,
        );

        const result =
          await warehousesApi.list({
            isActive: true,

            sort:
              "name_asc",

            limit: 100,
          });

        setWarehouses(
          result.items,
        );
      } catch (error) {
        console.error(
          "Warehouses load failed:",
          error,
        );

        setWarehouses(
          [],
        );
      } finally {
        setWarehousesLoading(
          false,
        );
      }
    },
    [
      canViewWarehouses,
    ],
  );


useEffect(
  () => {
    loadWarehouses();
  },
  [
    loadWarehouses,
  ],
);


const warehouseMap =
  useMemo(
    () =>
      new Map(
        warehouses.map(
          warehouse => [
            warehouse.id,
            warehouse,
          ],
        ),
      ),
    [
      warehouses,
    ],
  );


const getWarehouseName =
  useCallback(
    (
      id:
        string | null |
        undefined,
    ): string => {
      if (!id) {
        return "قديم / بدون مستودع";
      }

      const warehouse =
        warehouseMap.get(
          id,
        );

      return (
        warehouse?.nameAr ||
        warehouse?.name ||
        id
      );
    },
    [
      warehouseMap,
    ],
  );


const defaultWarehouseId =
  useMemo(
    () =>
      warehouses.find(
        warehouse =>
          warehouse.isDefault &&
          warehouse.isActive,
      )?.id ??
      warehouses.find(
        warehouse =>
          warehouse.isActive,
      )?.id ??
      "",
    [
      warehouses,
    ],
  );

    const handleSelectProduct =
      (
        product:
          AdminProduct,
      ) => {
        setSelectedProduct(
          product,
        );

        setProductId(
          product.id,
        );

        setProductSearch(
          product.name ||
          product.code ||
          product.id,
        );

        setProductSearchResults(
          [],
        );
      };


    const handleClearSelectedProduct =
      () => {
        setSelectedProduct(
          null,
        );

        setProductId(
          "",
        );

        setProductSearch(
          "",
        );

        setProductSearchResults(
          [],
        );
      };


    /* =====================================================
     * Transaction Details
     * =================================================== */

    const handleOpenTransactionDetails =
      (
        transaction:
          InventoryTransaction,
      ) => {
        setSelectedTransaction(
          transaction,
        );

        setOpenTransactionModal(
          true,
        );
      };


    const handleCloseTransactionDetails =
      () => {
        setOpenTransactionModal(
          false,
        );

        setSelectedTransaction(
          null,
        );
      };


    /* =====================================================
     * Reservation Details
     * =================================================== */

    const handleOpenReservationDetails =
      (
        reservation:
          InventoryReservation,
      ) => {
        setSelectedReservation(
          reservation,
        );

        setReleaseReason(
          "",
        );

        setTargetWarehouseId(
  "",
);

setReallocationQuantity(
  "",
);

setReallocationReason(
  "",
);

setReallocationIdempotencyKey(
  createReallocationIdempotencyKey(),
);

        setOpenReservationModal(
          true,
        );
      };


    const handleCloseReservationDetails =
      () => {
        if (
            releasingReservation ||
             reallocatingReservation
        ) {
          return;
        }

        setOpenReservationModal(
          false,
        );

        setSelectedReservation(
          null,
        );

        setReleaseReason(
          "",
        );

        setTargetWarehouseId(
  "",
);

setReallocationQuantity(
  "",
);

setReallocationReason(
  "",
);

setReallocationIdempotencyKey(
  createReallocationIdempotencyKey(),
);
      };


    /* =====================================================
     * Load Transactions
     * =================================================== */

    const loadTransactions =
      useCallback(
        async () => {
          try {
            setLoading(
              true,
            );

            const result =
              await inventoryApi.getTransactions(
                {
                  limit:
                    PAGE_SIZE,

                  ...(productId.trim()
                    ? {
                        productId:
                          productId.trim(),
                      }
                    : {}),

                    ...(warehouseId.trim()
  ? {
      warehouseId:
        warehouseId.trim(),
    }
  : {}),

                  ...(type
                    ? {
                        type,
                      }
                    : {}),

                  ...(source
                    ? {
                        source,
                      }
                    : {}),

                  ...(orderId.trim()
                    ? {
                        orderId:
                          orderId.trim(),
                      }
                    : {}),

                  ...(reservationId.trim()
                    ? {
                        reservationId:
                          reservationId.trim(),
                      }
                    : {}),

                  ...(performedBy.trim()
                    ? {
                        performedBy:
                          performedBy.trim(),
                      }
                    : {}),

                  ...(dateFrom
                    ? {
                        dateFrom,
                      }
                    : {}),

                  ...(dateTo
                    ? {
                        dateTo,
                      }
                    : {}),
                },
              );

            setTransactions(
              result.items,
            );

            setHasMore(
              result.hasMore,
            );

            setNextCursor(
              result.nextCursor,
            );
          } catch (error) {
            console.error(
              "Inventory transactions load failed:",
              error,
            );

            setTransactions(
              [],
            );

            setHasMore(
              false,
            );

            setNextCursor(
              null,
            );

            toast.error(
              getInventoryErrorMessage(
                error,
                "حدث خطأ أثناء تحميل سجل المخزون.",
              ),
            );
          } finally {
            setLoading(
              false,
            );
          }
        },
        [ 
  productId,
  warehouseId,
  type,
  source,
  orderId,
  reservationId,
  performedBy,
  dateFrom,
  dateTo,
        ],
      );


    useEffect(
      () => {
        if (
          activeInventoryTab ===
          "transactions"
        ) {
          loadTransactions();
        }
      },
      [
        activeInventoryTab,
        loadTransactions,
      ],
    );


    /* =====================================================
     * Load More Transactions
     * =================================================== */

    const handleLoadMoreTransactions =
      async () => {
        if (
          !nextCursor ||
          loadingMore
        ) {
          return;
        }

        try {
          setLoadingMore(
            true,
          );

          const result =
            await inventoryApi.getTransactions(
              {
                limit:
                  PAGE_SIZE,

                cursor:
                  nextCursor,

                ...(productId.trim()
                  ? {
                      productId:
                        productId.trim(),
                    }
                  : {}),

                  ...(warehouseId.trim()
  ? {
      warehouseId:
        warehouseId.trim(),
    }
  : {}),

                ...(type
                  ? {
                      type,
                    }
                  : {}),

                ...(source
                  ? {
                      source,
                    }
                  : {}),

                ...(orderId.trim()
                  ? {
                      orderId:
                        orderId.trim(),
                    }
                  : {}),

                ...(reservationId.trim()
                  ? {
                      reservationId:
                        reservationId.trim(),
                    }
                  : {}),

                ...(performedBy.trim()
                  ? {
                      performedBy:
                        performedBy.trim(),
                    }
                  : {}),

                ...(dateFrom
                  ? {
                      dateFrom,
                    }
                  : {}),

                ...(dateTo
                  ? {
                      dateTo,
                    }
                  : {}),
              },
            );

          setTransactions(
            (
              current,
            ) => {
              const map =
                new Map<
                  string,
                  InventoryTransaction
                >();

              current.forEach(
                (
                  transaction,
                ) => {
                  map.set(
                    transaction.transactionId,
                    transaction,
                  );
                },
              );

              result.items.forEach(
                (
                  transaction,
                ) => {
                  map.set(
                    transaction.transactionId,
                    transaction,
                  );
                },
              );

              return Array.from(
                map.values(),
              );
            },
          );

          setHasMore(
            result.hasMore,
          );

          setNextCursor(
            result.nextCursor,
          );
        } catch (error) {
          console.error(
            "Inventory transactions load more failed:",
            error,
          );

          toast.error(
            getInventoryErrorMessage(
              error,
              "حدث خطأ أثناء تحميل المزيد.",
            ),
          );
        } finally {
          setLoadingMore(
            false,
          );
        }
      };


    /* =====================================================
     * Load Reservations
     * =================================================== */

    const loadReservations =
      useCallback(
        async () => {
          try {
            setReservationsLoading(
              true,
            );

            const result =
              await inventoryApi.getReservations(
                {
                  limit:
                    PAGE_SIZE,

                  ...(productId.trim()
                    ? {
                        productId:
                          productId.trim(),
                      }
                    : {}),

                  ...(reservationStatus
                    ? {
                        status:
                          reservationStatus,
                      }
                    : {}),

                  ...(orderId.trim()
                    ? {
                        orderId:
                          orderId.trim(),
                      }
                    : {}),

                  ...(dateFrom
                    ? {
                        dateFrom,
                      }
                    : {}),

                  ...(dateTo
                    ? {
                        dateTo,
                      }
                    : {}),
                },
              );

            setReservations(
              result.items,
            );

            setReservationsHasMore(
              result.hasMore,
            );

            setReservationsNextCursor(
              result.nextCursor,
            );
          } catch (error) {
            console.error(
              "Inventory reservations load failed:",
              error,
            );

            setReservations(
              [],
            );

            setReservationsHasMore(
              false,
            );

            setReservationsNextCursor(
              null,
            );

            toast.error(
              getInventoryErrorMessage(
                error,
                "حدث خطأ أثناء تحميل حجوزات المخزون.",
              ),
            );
          } finally {
            setReservationsLoading(
              false,
            );
          }
        },
        [
          productId,
          reservationStatus,
          orderId,
          dateFrom,
          dateTo,
        ],
      );


    useEffect(
      () => {
        if (
          activeInventoryTab ===
          "reservations"
        ) {
          loadReservations();
        }
      },
      [
        activeInventoryTab,
        loadReservations,
      ],
    );


    /* =====================================================
     * Load More Reservations
     * =================================================== */

    const handleLoadMoreReservations =
      async () => {
        if (
          !reservationsNextCursor ||
          reservationsLoadingMore
        ) {
          return;
        }

        try {
          setReservationsLoadingMore(
            true,
          );

          const result =
            await inventoryApi.getReservations(
              {
                limit:
                  PAGE_SIZE,

                cursor:
                  reservationsNextCursor,

                ...(productId.trim()
                  ? {
                      productId:
                        productId.trim(),
                    }
                  : {}),

                ...(reservationStatus
                  ? {
                      status:
                        reservationStatus,
                    }
                  : {}),

                ...(orderId.trim()
                  ? {
                      orderId:
                        orderId.trim(),
                    }
                  : {}),

                ...(dateFrom
                  ? {
                      dateFrom,
                    }
                  : {}),

                ...(dateTo
                  ? {
                      dateTo,
                    }
                  : {}),
              },
            );

          setReservations(
            (
              current,
            ) => {
              const map =
                new Map<
                  string,
                  InventoryReservation
                >();

              current.forEach(
                (
                  reservation,
                ) => {
                  map.set(
                    reservation.reservationId,
                    reservation,
                  );
                },
              );

              result.items.forEach(
                (
                  reservation,
                ) => {
                  map.set(
                    reservation.reservationId,
                    reservation,
                  );
                },
              );

              return Array.from(
                map.values(),
              );
            },
          );

          setReservationsHasMore(
            result.hasMore,
          );

          setReservationsNextCursor(
            result.nextCursor,
          );
        } catch (error) {
          console.error(
            "Inventory reservations load more failed:",
            error,
          );

          toast.error(
            getInventoryErrorMessage(
              error,
              "حدث خطأ أثناء تحميل المزيد من الحجوزات.",
            ),
          );
        } finally {
          setReservationsLoadingMore(
            false,
          );
        }
      };

      /* =====================================================
 * Load Warehouse Stock
 * =================================================== */

const loadWarehouseStocks =
  useCallback(
    async () => {
      try {
        setWarehouseStocksLoading(
          true,
        );

        const result =
          await inventoryApi
            .getWarehouseStocks({
              ...(warehouseId.trim()
                ? {
                    warehouseId:
                      warehouseId.trim(),
                  }
                : {}),

              ...(productId.trim()
                ? {
                    productId:
                      productId.trim(),
                  }
                : {}),

              ...(warehouseStockAvailability ===
              "in-stock"
                ? {
                    inStock:
                      true,
                  }
                : warehouseStockAvailability ===
                    "out-of-stock"
                  ? {
                      inStock:
                        false,
                    }
                  : {}),

              limit:
                PAGE_SIZE,
            });


        setWarehouseStocks(
          result.items,
        );

        setWarehouseStocksHasMore(
          result.pagination
            .hasMore,
        );

        setWarehouseStocksNextCursor(
          result.pagination
            .nextCursor,
        );
      } catch (error) {
        console.error(
          "Warehouse stock load failed:",
          error,
        );

        setWarehouseStocks(
          [],
        );

        setWarehouseStocksHasMore(
          false,
        );

        setWarehouseStocksNextCursor(
          null,
        );

        toast.error(
          getInventoryErrorMessage(
            error,
            "حدث خطأ أثناء تحميل مخزون المستودعات.",
          ),
        );
      } finally {
        setWarehouseStocksLoading(
          false,
        );
      }
    },
    [
      warehouseId,
      productId,
      warehouseStockAvailability,
    ],
  );


useEffect(
  () => {
    if (
      activeInventoryTab ===
      "warehouse-stock"
    ) {
      loadWarehouseStocks();
    }
  },
  [
    activeInventoryTab,
    loadWarehouseStocks,
  ],
);


/* =====================================================
 * Load More Warehouse Stock
 * =================================================== */

const handleLoadMoreWarehouseStocks =
  async () => {
    if (
      !warehouseStocksNextCursor ||
      warehouseStocksLoadingMore
    ) {
      return;
    }

    try {
      setWarehouseStocksLoadingMore(
        true,
      );

      const result =
        await inventoryApi
          .getWarehouseStocks({
            cursor:
              warehouseStocksNextCursor,

            ...(warehouseId.trim()
              ? {
                  warehouseId:
                    warehouseId.trim(),
                }
              : {}),

            ...(productId.trim()
              ? {
                  productId:
                    productId.trim(),
                }
              : {}),

            ...(warehouseStockAvailability ===
            "in-stock"
              ? {
                  inStock: true,
                }
              : warehouseStockAvailability ===
                  "out-of-stock"
                ? {
                    inStock: false,
                  }
                : {}),

            limit:
              PAGE_SIZE,
          });


      setWarehouseStocks(
        current => {
          const map =
            new Map<
              string,
              WarehouseStockRecord
            >();

          current.forEach(
            stock =>
              map.set(
                stock.stockId,
                stock,
              ),
          );

          result.items.forEach(
            stock =>
              map.set(
                stock.stockId,
                stock,
              ),
          );

          return Array.from(
            map.values(),
          );
        },
      );


      setWarehouseStocksHasMore(
        result.pagination
          .hasMore,
      );

      setWarehouseStocksNextCursor(
        result.pagination
          .nextCursor,
      );
    } catch (error) {
      console.error(
        "Warehouse stock load more failed:",
        error,
      );

      toast.error(
        getInventoryErrorMessage(
          error,
          "حدث خطأ أثناء تحميل المزيد من مخزون المستودعات.",
        ),
      );
    } finally {
      setWarehouseStocksLoadingMore(
        false,
      );
    }
  };

    /* =====================================================
     * Release Reservation
     * =================================================== */

    const handleReleaseReservation =
      async () => {
        if (
          !selectedReservation
        ) {
          return;
        }

        if (
          selectedReservation.status !==
          "active"
        ) {
          toast.error(
            "هذا الحجز غير نشط.",
          );

          return;
        }

        const normalizedReason =
          releaseReason.trim();

        if (
          !normalizedReason
        ) {
          toast.error(
            "اكتب سبب تحرير الحجز.",
          );

          return;
        }

        try {
          setReleasingReservation(
            true,
          );

          const result =
            await inventoryApi.releaseReservation(
              selectedReservation.reservationId,
              {
                reason:
                  normalizedReason,
              },
            );

          const updatedReservation =
            result.reservation;

          setReservations(
            (
              current,
            ) =>
              current.map(
                (
                  reservation,
                ) =>
                  reservation.reservationId ===
                  updatedReservation.reservationId
                    ? updatedReservation
                    : reservation,
              ),
          );

          setSelectedReservation(
            updatedReservation,
          );

          setReleaseReason(
            "",
          );

          toast.success(
            "تم تحرير الحجز وإرجاع الكمية إلى المخزون بنجاح.",
          );

          await loadReservations();
        } catch (error) {
          console.error(
            "Release inventory reservation failed:",
            error,
          );

          toast.error(
            getInventoryErrorMessage(
              error,
              "حدث خطأ أثناء تحرير الحجز.",
            ),
          );
        } finally {
          setReleasingReservation(
            false,
          );
        }
      };



      /* =====================================================
 * Reallocate Reservation
 * =================================================== */

const handleReallocateReservation =
  async () => {
    if (
      !selectedReservation
    ) {
      return;
    }


    if (
      selectedReservation.status !==
      "active"
    ) {
      toast.error(
        "هذا الحجز غير نشط.",
      );

      return;
    }


    if (
      !selectedReservation
        .warehouseId
    ) {
      toast.error(
        "هذا الحجز قديم وغير مرتبط بمستودع.",
      );

      return;
    }


    if (
      !targetWarehouseId.trim()
    ) {
      toast.error(
        "اختر المستودع الهدف.",
      );

      return;
    }


    if (
      targetWarehouseId ===
      selectedReservation
        .warehouseId
    ) {
      toast.error(
        "اختر مستودعًا مختلفًا عن المستودع الحالي.",
      );

      return;
    }


    const normalizedReason =
      reallocationReason
        .trim();


    if (!normalizedReason) {
      toast.error(
        "اكتب سبب إعادة توزيع الحجز.",
      );

      return;
    }


    let quantity:
      number | undefined;


    if (
      reallocationQuantity
        .trim()
    ) {
      quantity =
        Number(
          reallocationQuantity,
        );


      if (
        !Number.isInteger(
          quantity,
        ) ||
        quantity <= 0
      ) {
        toast.error(
          "كمية النقل يجب أن تكون رقمًا صحيحًا أكبر من صفر.",
        );

        return;
      }


      if (
        quantity >
        selectedReservation
          .quantity
      ) {
        toast.error(
          "كمية النقل أكبر من كمية الحجز.",
        );

        return;
      }
    }


    try {
      setReallocatingReservation(
        true,
      );


      const result =
        await inventoryApi
          .reallocateReservation(
            selectedReservation
              .reservationId,

            {
              idempotencyKey:
                reallocationIdempotencyKey,

              targetWarehouseId:
                targetWarehouseId
                  .trim(),

              ...(quantity !==
              undefined
                ? {
                    quantity,
                  }
                : {}),

              reason:
                normalizedReason,
            },
          );


      toast.success(
        `تم نقل ${result.reallocation.quantity} من ${getWarehouseName(
          result.reallocation
            .sourceWarehouseId,
        )} إلى ${getWarehouseName(
          result.reallocation
            .targetWarehouseId,
        )}.`,
      );


      setOpenReservationModal(
        false,
      );

      setSelectedReservation(
        null,
      );


      await Promise.all([
        loadReservations(),

        loadTransactions(),

        loadWarehouseStocks(),
      ]);
    } catch (error) {
      console.error(
        "Reservation reallocation failed:",
        error,
      );


      toast.error(
        getInventoryErrorMessage(
          error,
          "حدث خطأ أثناء إعادة توزيع الحجز.",
        ),
      );
    } finally {
      setReallocatingReservation(
        false,
      );
    }
  };
    /* =====================================================
     * Manual Inventory Adjustment
     * =================================================== */

const handleOpenAdjustmentModal =
  () => {
    if (!selectedProduct) {
      toast.error(
        "اختر المنتج من فلتر المنتج أولًا، ثم اضغط تسوية الجرد.",
      );
      return;
    }

    const preferredWarehouseId =
      warehouseId &&
      warehouses.some(
        warehouse =>
          warehouse.id === warehouseId &&
          warehouse.isActive,
      )
        ? warehouseId
        : defaultWarehouseId;

    setAdjustmentWarehouseId(
      preferredWarehouseId,
    );
    setAdjustmentWarehouseStock(
      null,
    );
    setAdjustmentQuantity("");
    setAdjustmentReason("");
    setAdjustmentOrderId("");
    setAdjustmentReferenceId("");
    setOpenAdjustmentModal(true);
  };


const handleCloseAdjustmentModal =
  () => {
    if (adjustmentProcessing) {
      return;
    }

    setOpenAdjustmentModal(false);
    setAdjustmentWarehouseId("");
    setAdjustmentWarehouseStock(null);
    setAdjustmentQuantity("");
    setAdjustmentReason("");
    setAdjustmentOrderId("");
    setAdjustmentReferenceId("");
  };


/**
 * Load fresh warehouse stock directly for stock-count reconciliation.
 * This does not depend on the warehouse-stock tab or its pagination.
 */
useEffect(
  () => {
    if (
      !openAdjustmentModal ||
      !selectedProduct ||
      !adjustmentWarehouseId
    ) {
      setAdjustmentWarehouseStock(null);
      return;
    }

    let cancelled = false;

    const loadStock =
      async () => {
        try {
          setAdjustmentWarehouseStockLoading(true);
          setAdjustmentWarehouseStock(null);

          const stock =
            await inventoryApi.getWarehouseProductStock(
              adjustmentWarehouseId,
              selectedProduct.id,
            );

          if (!cancelled) {
            setAdjustmentWarehouseStock(stock);
          }
        } catch (error) {
          if (!cancelled) {
            console.error(
              "Stock-count warehouse stock load failed:",
              error,
            );
            setAdjustmentWarehouseStock(null);
            toast.error(
              getInventoryErrorMessage(
                error,
                "تعذر تحميل مخزون المنتج في المستودع المحدد.",
              ),
            );
          }
        } finally {
          if (!cancelled) {
            setAdjustmentWarehouseStockLoading(false);
          }
        }
      };

    loadStock();

    return () => {
      cancelled = true;
    };
  },
  [
    openAdjustmentModal,
    selectedProduct,
    adjustmentWarehouseId,
  ],
);


const adjustmentCurrentAvailable =
  adjustmentWarehouseStock?.quantity ?? 0;

const adjustmentCurrentReserved =
  adjustmentWarehouseStock?.reservedQuantity ?? 0;

const adjustmentCurrentOnHand =
  adjustmentWarehouseStock?.onHandQuantity ??
  (
    adjustmentCurrentAvailable +
    adjustmentCurrentReserved
  );

const adjustmentCountedQuantity =
  adjustmentQuantity.trim() !== ""
    ? Number(adjustmentQuantity)
    : null;

const adjustmentDelta =
  adjustmentCountedQuantity !== null &&
  Number.isInteger(adjustmentCountedQuantity)
    ? adjustmentCountedQuantity -
      adjustmentCurrentOnHand
    : null;

const adjustmentNewAvailable =
  adjustmentDelta !== null
    ? adjustmentCurrentAvailable +
      adjustmentDelta
    : null;

const adjustmentNewOnHand =
  adjustmentCountedQuantity !== null &&
  Number.isInteger(adjustmentCountedQuantity)
    ? adjustmentCountedQuantity
    : null;


const handleCreateAdjustment =
  async () => {
    if (!selectedProduct) {
      toast.error("اختر المنتج أولًا.");
      return;
    }

    if (!adjustmentWarehouseId.trim()) {
      toast.error("اختر المستودع.");
      return;
    }

    if (adjustmentWarehouseStockLoading) {
      toast("انتظر حتى يتم تحميل مخزون المستودع.");
      return;
    }

    if (!adjustmentWarehouseStock) {
      toast.error(
        "لا توجد بيانات مخزون لهذا المنتج في المستودع المحدد.",
      );
      return;
    }

    const countedQuantity =
      Number(adjustmentQuantity);

    if (
      !Number.isInteger(countedQuantity) ||
      countedQuantity < 0
    ) {
      toast.error(
        "الكمية الفعلية بعد الجرد يجب أن تكون رقمًا صحيحًا صفر أو أكبر.",
      );
      return;
    }

    const delta =
      countedQuantity -
      adjustmentCurrentOnHand;

    if (delta === 0) {
      toast(
        "لا يوجد فرق بين الجرد الفعلي والمخزون المسجل.",
      );
      return;
    }

    const newAvailableQuantity =
      adjustmentCurrentAvailable +
      delta;

    if (newAvailableQuantity < 0) {
      toast.error(
        `لا يمكن تنفيذ التسوية لأن نتيجة الجرد أقل من الكمية المحجوزة (${adjustmentCurrentReserved}). عالج الحجوزات أولًا.`,
      );
      return;
    }

    const reason =
      adjustmentReason.trim();

    if (!reason) {
      toast.error(
        "اكتب سبب تسوية المخزون.",
      );
      return;
    }

    try {
      setAdjustmentProcessing(true);

      await inventoryApi.createAdjustment({
        productId:
          selectedProduct.id,
        warehouseId:
          adjustmentWarehouseId,
        delta,
        reason,
        ...(adjustmentOrderId.trim()
          ? {
              orderId:
                adjustmentOrderId.trim(),
            }
          : {}),
        ...(adjustmentReferenceId.trim()
          ? {
              referenceId:
                adjustmentReferenceId.trim(),
            }
          : {}),
      });

      toast.success(
        delta > 0
          ? `تمت تسوية المخزون بزيادة ${delta} وحدة. المخزون الفعلي أصبح ${countedQuantity}.`
          : `تمت تسوية المخزون بنقص ${Math.abs(delta)} وحدة. المخزون الفعلي أصبح ${countedQuantity}.`,
      );

      setOpenAdjustmentModal(false);
      setAdjustmentWarehouseId("");
      setAdjustmentWarehouseStock(null);
      setAdjustmentQuantity("");
      setAdjustmentReason("");
      setAdjustmentOrderId("");
      setAdjustmentReferenceId("");

      await Promise.all([
        loadTransactions(),
        loadWarehouseStocks(),
      ]);
    } catch (error) {
      console.error(
        "Inventory stock-count reconciliation failed:",
        error,
      );

      toast.error(
        getInventoryErrorMessage(
          error,
          "حدث خطأ أثناء تسوية المخزون.",
        ),
      );
    } finally {
      setAdjustmentProcessing(false);
    }
  };


/* =====================================================
 * Manual Inventory Adjustment
 * =================================================== */

const handleOpenManualAdjustmentModal =
  () => {
    if (!selectedProduct) {
      toast.error(
        "اختر المنتج من فلتر المنتج أولًا، ثم اضغط تعديل يدوي.",
      );
      return;
    }

    const preferredWarehouseId =
      warehouseId &&
      warehouses.some(
        warehouse =>
          warehouse.id === warehouseId &&
          warehouse.isActive,
      )
        ? warehouseId
        : defaultWarehouseId;

    setManualAdjustmentWarehouseId(
      preferredWarehouseId,
    );
    setManualAdjustmentMode("increase");
    setManualAdjustmentQuantity("");
    setManualAdjustmentReason("");
    setManualAdjustmentOrderId("");
    setManualAdjustmentReferenceId("");
    setManualAdjustmentWarehouseStock(null);
    setOpenManualAdjustmentModal(true);
  };


const handleCloseManualAdjustmentModal =
  () => {
    if (manualAdjustmentProcessing) {
      return;
    }

    setOpenManualAdjustmentModal(false);
    setManualAdjustmentWarehouseId("");
    setManualAdjustmentMode("increase");
    setManualAdjustmentQuantity("");
    setManualAdjustmentReason("");
    setManualAdjustmentOrderId("");
    setManualAdjustmentReferenceId("");
    setManualAdjustmentWarehouseStock(null);
  };


useEffect(
  () => {
    if (
      !openManualAdjustmentModal ||
      !selectedProduct ||
      !manualAdjustmentWarehouseId
    ) {
      setManualAdjustmentWarehouseStock(null);
      return;
    }

    let cancelled = false;

    const loadStock =
      async () => {
        try {
          setManualAdjustmentWarehouseStockLoading(true);
          setManualAdjustmentWarehouseStock(null);

          const stock =
            await inventoryApi.getWarehouseProductStock(
              manualAdjustmentWarehouseId,
              selectedProduct.id,
            );

          if (!cancelled) {
            setManualAdjustmentWarehouseStock(stock);
          }
        } catch (error) {
          if (!cancelled) {
            console.error(
              "Manual adjustment warehouse stock load failed:",
              error,
            );
            setManualAdjustmentWarehouseStock(null);
            toast.error(
              getInventoryErrorMessage(
                error,
                "تعذر تحميل مخزون المنتج في المستودع المحدد.",
              ),
            );
          }
        } finally {
          if (!cancelled) {
            setManualAdjustmentWarehouseStockLoading(false);
          }
        }
      };

    loadStock();

    return () => {
      cancelled = true;
    };
  },
  [
    openManualAdjustmentModal,
    selectedProduct,
    manualAdjustmentWarehouseId,
  ],
);


const manualAdjustmentCurrentAvailable =
  manualAdjustmentWarehouseStock?.quantity ?? 0;

const manualAdjustmentCurrentReserved =
  manualAdjustmentWarehouseStock?.reservedQuantity ?? 0;

const manualAdjustmentCurrentOnHand =
  manualAdjustmentWarehouseStock?.onHandQuantity ??
  (
    manualAdjustmentCurrentAvailable +
    manualAdjustmentCurrentReserved
  );

const manualAdjustmentParsedQuantity =
  manualAdjustmentQuantity.trim() !== ""
    ? Number(manualAdjustmentQuantity)
    : null;

const manualAdjustmentDelta =
  manualAdjustmentParsedQuantity !== null &&
  Number.isInteger(manualAdjustmentParsedQuantity) &&
  manualAdjustmentParsedQuantity > 0
    ? manualAdjustmentMode === "increase"
      ? manualAdjustmentParsedQuantity
      : -manualAdjustmentParsedQuantity
    : null;

const manualAdjustmentNewAvailable =
  manualAdjustmentDelta !== null
    ? manualAdjustmentCurrentAvailable +
      manualAdjustmentDelta
    : null;

const manualAdjustmentNewOnHand =
  manualAdjustmentDelta !== null
    ? manualAdjustmentCurrentOnHand +
      manualAdjustmentDelta
    : null;


const handleCreateManualAdjustment =
  async () => {
    if (!selectedProduct) {
      toast.error("اختر المنتج أولًا.");
      return;
    }

    if (!manualAdjustmentWarehouseId.trim()) {
      toast.error("اختر المستودع.");
      return;
    }

    if (manualAdjustmentWarehouseStockLoading) {
      toast("انتظر حتى يتم تحميل مخزون المستودع.");
      return;
    }

    if (!manualAdjustmentWarehouseStock) {
      toast.error(
        "لا توجد بيانات مخزون لهذا المنتج في المستودع المحدد.",
      );
      return;
    }

    const quantity =
      Number(manualAdjustmentQuantity);

    if (
      !Number.isInteger(quantity) ||
      quantity <= 0
    ) {
      toast.error(
        "الكمية يجب أن تكون رقمًا صحيحًا أكبر من صفر.",
      );
      return;
    }

    const delta =
      manualAdjustmentMode === "increase"
        ? quantity
        : -quantity;

    if (
      manualAdjustmentCurrentAvailable +
        delta <
      0
    ) {
      toast.error(
        `لا يمكن خصم ${quantity} وحدة. المتاح حاليًا في هذا المستودع هو ${manualAdjustmentCurrentAvailable} وحدة فقط.`,
      );
      return;
    }

    const reason =
      manualAdjustmentReason.trim();

    if (!reason) {
      toast.error(
        "اكتب سبب التعديل اليدوي.",
      );
      return;
    }

    try {
      setManualAdjustmentProcessing(true);

      await inventoryApi.createAdjustment({
        productId:
          selectedProduct.id,
        warehouseId:
          manualAdjustmentWarehouseId,
        delta,
        reason,
        ...(manualAdjustmentOrderId.trim()
          ? {
              orderId:
                manualAdjustmentOrderId.trim(),
            }
          : {}),
        ...(manualAdjustmentReferenceId.trim()
          ? {
              referenceId:
                manualAdjustmentReferenceId.trim(),
            }
          : {}),
      });

      toast.success(
        delta > 0
          ? `تمت إضافة ${quantity} وحدة إلى المخزون يدويًا.`
          : `تم خصم ${quantity} وحدة من المخزون يدويًا.`,
      );

      setOpenManualAdjustmentModal(false);
      setManualAdjustmentWarehouseId("");
      setManualAdjustmentWarehouseStock(null);
      setManualAdjustmentQuantity("");
      setManualAdjustmentReason("");
      setManualAdjustmentOrderId("");
      setManualAdjustmentReferenceId("");

      await Promise.all([
        loadTransactions(),
        loadWarehouseStocks(),
      ]);
    } catch (error) {
      console.error(
        "Manual inventory adjustment failed:",
        error,
      );

      toast.error(
        getInventoryErrorMessage(
          error,
          "حدث خطأ أثناء تنفيذ التعديل اليدوي للمخزون.",
        ),
      );
    } finally {
      setManualAdjustmentProcessing(false);
    }
  };


    /* =====================================================
     * Create Reservation
     * =================================================== */

const createReservationIdempotencyKey =
  () =>
    [
      "dashboard-reservation",
      Date.now(),
      Math.random()
        .toString(36)
        .slice(2, 12),
    ].join("-");


const handleOpenCreateReservationModal =
  () => {
    if (!selectedProduct) {
      toast.error(
        "اختر المنتج من فلتر المنتج أولًا.",
      );
      return;
    }

    setReservationWarehouseId(
      defaultWarehouseId,
    );
    setReservationQuantity("");
    setReservationReason("");
    setReservationExpiresAt("");
    setReservationWarehouseStock(null);
    setReservationIdempotencyKey(
      createReservationIdempotencyKey(),
    );
    setOpenCreateReservationModal(true);
  };


const handleCloseCreateReservationModal =
  () => {
    if (reservationCreating) {
      return;
    }

    setOpenCreateReservationModal(false);
    setReservationWarehouseId("");
    setReservationQuantity("");
    setReservationReason("");
    setReservationExpiresAt("");
    setReservationWarehouseStock(null);
  };


useEffect(
  () => {
    if (
      !openCreateReservationModal ||
      !selectedProduct ||
      !reservationWarehouseId
    ) {
      setReservationWarehouseStock(null);
      return;
    }

    let cancelled = false;

    const loadStock =
      async () => {
        try {
          setReservationWarehouseStockLoading(true);
          setReservationWarehouseStock(null);

          const stock =
            await inventoryApi.getWarehouseProductStock(
              reservationWarehouseId,
              selectedProduct.id,
            );

          if (!cancelled) {
            setReservationWarehouseStock(stock);
          }
        } catch (error) {
          if (!cancelled) {
            console.error(
              "Reservation warehouse stock load failed:",
              error,
            );

            setReservationWarehouseStock(null);

            toast.error(
              getInventoryErrorMessage(
                error,
                "تعذر تحميل مخزون المنتج في المستودع المحدد.",
              ),
            );
          }
        } finally {
          if (!cancelled) {
            setReservationWarehouseStockLoading(false);
          }
        }
      };

    loadStock();

    return () => {
      cancelled = true;
    };
  },
  [
    openCreateReservationModal,
    selectedProduct,
    reservationWarehouseId,
  ],
);


const reservationCurrentAvailable =
  reservationWarehouseStock?.quantity ?? 0;

const reservationCurrentReserved =
  reservationWarehouseStock?.reservedQuantity ?? 0;

const reservationCurrentOnHand =
  reservationWarehouseStock?.onHandQuantity ??
  (
    reservationCurrentAvailable +
    reservationCurrentReserved
  );

const reservationParsedQuantity =
  reservationQuantity.trim() !== ""
    ? Number(reservationQuantity)
    : null;

const reservationAvailableAfter =
  reservationParsedQuantity !== null &&
  Number.isInteger(reservationParsedQuantity) &&
  reservationParsedQuantity > 0
    ? reservationCurrentAvailable -
      reservationParsedQuantity
    : null;

const reservationReservedAfter =
  reservationParsedQuantity !== null &&
  Number.isInteger(reservationParsedQuantity) &&
  reservationParsedQuantity > 0
    ? reservationCurrentReserved +
      reservationParsedQuantity
    : null;


const handleCreateReservation =
  async () => {
    if (!selectedProduct) {
      toast.error("اختر المنتج أولًا.");
      return;
    }

    if (!reservationWarehouseId.trim()) {
      toast.error("اختر المستودع.");
      return;
    }

    if (reservationWarehouseStockLoading) {
      toast(
        "انتظر حتى يتم تحميل مخزون المستودع.",
      );
      return;
    }

    if (!reservationWarehouseStock) {
      toast.error(
        "لا توجد بيانات مخزون لهذا المنتج في المستودع المحدد.",
      );
      return;
    }

    const quantity =
      Number(reservationQuantity);

    if (
      !Number.isInteger(quantity) ||
      quantity <= 0
    ) {
      toast.error(
        "كمية الحجز يجب أن تكون رقمًا صحيحًا أكبر من صفر.",
      );
      return;
    }

    if (
      quantity >
      reservationCurrentAvailable
    ) {
      toast.error(
        `لا يمكن حجز ${quantity} وحدة. المتاح حاليًا في هذا المستودع هو ${reservationCurrentAvailable} وحدة فقط.`,
      );
      return;
    }

    const reason =
      reservationReason.trim();

    if (!reason) {
      toast.error("اكتب سبب الحجز.");
      return;
    }

    let expiresAt:
      string | undefined;

    if (reservationExpiresAt) {
      const expiresDate =
        new Date(
          reservationExpiresAt,
        );

      if (
        Number.isNaN(
          expiresDate.getTime(),
        )
      ) {
        toast.error(
          "تاريخ انتهاء الحجز غير صحيح.",
        );
        return;
      }

      if (
        expiresDate.getTime() <=
        Date.now()
      ) {
        toast.error(
          "تاريخ انتهاء الحجز يجب أن يكون في المستقبل.",
        );
        return;
      }

      expiresAt =
        expiresDate.toISOString();
    }

    try {
      setReservationCreating(true);

      await inventoryApi.createReservation({
        productId:
          selectedProduct.id,
        warehouseId:
          reservationWarehouseId,
        quantity,
        reason,
        idempotencyKey:
          reservationIdempotencyKey,
        ...(expiresAt
          ? {
              expiresAt,
            }
          : {}),
      });

      toast.success(
        `تم حجز ${quantity} وحدة من ${selectedProduct.name || selectedProduct.code || "المنتج"} بنجاح.`,
      );

      setOpenCreateReservationModal(false);
      setReservationWarehouseId("");
      setReservationQuantity("");
      setReservationReason("");
      setReservationExpiresAt("");
      setReservationWarehouseStock(null);
      setReservationIdempotencyKey(
        createReservationIdempotencyKey(),
      );

      await Promise.all([
        loadReservations(),
        loadTransactions(),
        loadWarehouseStocks(),
      ]);
    } catch (error) {
      console.error(
        "Inventory reservation creation failed:",
        error,
      );

      toast.error(
        getInventoryErrorMessage(
          error,
          "حدث خطأ أثناء إنشاء حجز المخزون.",
        ),
      );
    } finally {
      setReservationCreating(false);
    }
  };


    /* =====================================================
     * Reset Filters
     * =================================================== */

    const handleResetFilters =
      () => {
        setProductSearch(
          "",
        );

        setProductSearchResults(
          [],
        );

        setSelectedProduct(
          null,
        );

        setProductId(
          "",
        );

        setOrderId(
          "",
        );

        setDateFrom(
          "",
        );

        setDateTo(
          "",
        );

        setType(
          "",
        );

        setSource(
          "",
        );

        setReservationId(
          "",
        );

        setPerformedBy(
          "",
        );

        setReservationStatus(
          "",
        );

        setWarehouseId(
  "",
);

setWarehouseStockAvailability(
  "all",
);
      };


    /* =====================================================
     * Refresh
     * =================================================== */

   const handleRefresh =
  () => {
    if (
      activeInventoryTab ===
      "transactions"
    ) {
      loadTransactions();

      return;
    }


    if (
      activeInventoryTab ===
      "reservations"
    ) {
      loadReservations();

      return;
    }


    loadWarehouseStocks();
  };


    /* =====================================================
     * Transaction Summary
     * =================================================== */

   const summary =
  useMemo(
    () => {
      let positive = 0;
      let negative = 0;
      let zero = 0;

      transactions.forEach(
        (
          transaction,
        ) => {
          const {
            delta,
          } =
            getDisplayedTransactionQuantities(
              transaction,
            );

          if (
            delta > 0
          ) {
            positive += 1;
          } else if (
            delta < 0
          ) {
            negative += 1;
          } else {
            zero += 1;
          }
        },
      );

      return {
        total:
          transactions.length,

        positive,

        negative,

        zero,
      };
    },
    [
      transactions,
    ],
  );



      const warehouseStockSummary =
  useMemo(
    () => {
      return warehouseStocks
        .reduce(
          (
            result,
            stock,
          ) => {
            result.available +=
              stock.quantity;

            result.reserved +=
              stock.reservedQuantity;

            result.onHand +=
              stock.onHandQuantity;

            return result;
          },
          {
            available: 0,
            reserved: 0,
            onHand: 0,
          },
        );
    },
    [
      warehouseStocks,
    ],
  );
    /* =====================================================
     * Render
     * =================================================== */

    return (

      <div className="inventory-page">

        {/* =================================================
         * Header
         * =============================================== */}

        <div className="inventory-header">

          <div>

            <h1 className="inventory-title">
              إدارة المخزون
            </h1>

            <p className="inventory-subtitle">

            {activeInventoryTab ===
"transactions"
  ? "سجل مركزي لجميع حركات وتغييرات المخزون."
  : activeInventoryTab ===
      "reservations"
    ? "إدارة ومتابعة حجوزات كميات المنتجات في المخزون."
    : "عرض الكميات المتاحة والمحجوزة والفعلية لكل مستودع."}
            </p>

          </div>


          {activeInventoryTab ===
            "reservations" &&
            canReserveInventory && (
              <button
                type="button"
                className="inventory-refresh-btn"
                onClick={
                  handleOpenCreateReservationModal
                }
                disabled={
                  !selectedProduct ||
                  warehousesLoading
                }
                title={
                  selectedProduct
                    ? "إنشاء حجز مخزون للمنتج المحدد"
                    : "اختر المنتج من الفلتر أولًا"
                }
                style={{
                  marginLeft: "8px",
                }}
              >
                <FontAwesomeIcon
                  icon={faBoxesStacked}
                />
                إنشاء حجز
              </button>
            )}


          {canAdjustInventory && (
            <>
              <button
                type="button"
                className="inventory-refresh-btn"
                onClick={
                  handleOpenAdjustmentModal
                }
                disabled={
                  !selectedProduct ||
                  warehousesLoading
                }
                title={
                  selectedProduct
                    ? "إدخال نتيجة الجرد الفعلي وتسوية المخزون"
                    : "اختر المنتج من الفلتر أولًا"
                }
                style={{
                  marginLeft: "8px",
                }}
              >
                <FontAwesomeIcon
                  icon={faBoxesStacked}
                />
                تسوية الجرد
              </button>

              <button
                type="button"
                className="inventory-refresh-btn"
                onClick={
                  handleOpenManualAdjustmentModal
                }
                disabled={
                  !selectedProduct ||
                  warehousesLoading
                }
                title={
                  selectedProduct
                    ? "إضافة أو خصم كمية من المخزون يدويًا"
                    : "اختر المنتج من الفلتر أولًا"
                }
                style={{
                  marginLeft: "8px",
                }}
              >
                <FontAwesomeIcon
                  icon={faBoxesStacked}
                />
                تعديل يدوي
              </button>
            </>
          )}


          <button
            type="button"
            className="inventory-refresh-btn"
            onClick={
              handleRefresh
            }
            disabled={
  activeInventoryTab ===
  "transactions"
    ? loading
    : activeInventoryTab ===
        "reservations"
      ? reservationsLoading
      : warehouseStocksLoading
}
          >

            <FontAwesomeIcon
              icon={
                faRotateLeft
              }
            />

            تحديث

          </button>

        </div>


        {/* =================================================
         * Tabs
         * =============================================== */}

        <div className="inventory-tabs">

          <button
            type="button"
            className={
              activeInventoryTab ===
              "transactions"
                ? "active"
                : ""
            }
            onClick={() =>
              setActiveInventoryTab(
                "transactions",
              )
            }
          >
            حركات المخزون
          </button>


          <button
            type="button"
            className={
              activeInventoryTab ===
              "reservations"
                ? "active"
                : ""
            }
            onClick={() =>
              setActiveInventoryTab(
                "reservations",
              )
            }
          >
            الحجوزات
          </button>

<button
  type="button"
  className={
    activeInventoryTab ===
    "warehouse-stock"
      ? "active"
      : ""
  }
  onClick={() =>
    setActiveInventoryTab(
      "warehouse-stock",
    )
  }
>
  مخزون المستودعات
</button>
        </div>


        {/* =================================================
         * Filters
         * =============================================== */}

        <div className="inventory-filters">

          <div className="inventory-filters-header">

            <div>

              <FontAwesomeIcon
                icon={
                  faFilter
                }
              />

              <strong>

            {activeInventoryTab ===
"transactions"
  ? "فلترة حركات المخزون"
  : activeInventoryTab ===
      "reservations"
    ? "فلترة حجوزات المخزون"
    : "فلترة مخزون المستودعات"}

              </strong>

            </div>


            <button
              type="button"
              onClick={
                handleResetFilters
              }
              className="inventory-reset-btn"
            >
              إعادة التعيين
            </button>

          </div>


          <div className="inventory-filters-grid">


            {/* =============================================
             * Product
             * =========================================== */}

            <div className="inventory-filter-field inventory-product-search-field">

              <label>
                المنتج
              </label>


              <div className="inventory-product-search">

                <div className="inventory-input-with-icon">

                  <FontAwesomeIcon
                    icon={
                      faSearch
                    }
                  />

                  <input
                    type="text"
                    value={
                      productSearch
                    }
                    onChange={(
                      event,
                    ) => {
                      setProductSearch(
                        event.target.value,
                      );

                      if (
                        selectedProduct
                      ) {
                        setSelectedProduct(
                          null,
                        );

                        setProductId(
                          "",
                        );
                      }
                    }}
                    placeholder="ابحث باسم المنتج أو الكود"
                  />

                </div>


                {selectedProduct && (

                  <button
                    type="button"
                    className="inventory-product-clear-btn"
                    onClick={
                      handleClearSelectedProduct
                    }
                  >
                    إزالة الاختيار
                  </button>

                )}


                {!selectedProduct &&
                  (
                    productSearchLoading ||
                    productSearchResults.length >
                      0
                  ) && (

                  <div className="inventory-product-search-results">

                    {productSearchLoading ? (

                      <div className="inventory-product-search-loading">
                        جاري البحث...
                      </div>

                    ) : (

                      productSearchResults.map(
                        (
                          product,
                        ) => (

                          <button
                            type="button"
                            key={
                              product.id
                            }
                            className="inventory-product-search-item"
                            onClick={() =>
                              handleSelectProduct(
                                product,
                              )
                            }
                          >

                            <div>

                              <strong>
                                {product.name ||
                                  "-"}
                              </strong>

                              <span>
                                {product.code ||
                                  product.id}
                              </span>

                            </div>


                            <span className="inventory-product-search-stock">

                              المخزون:
                              {" "}

                              {product.quantity ??
                                product.Quantity ??
                                0}

                            </span>

                          </button>

                        ),
                      )

                    )}

                  </div>

                )}

              </div>

            </div>


{activeInventoryTab !==
  "reservations" && (

  <div className="inventory-filter-field">

    <label>
      المستودع
    </label>

    <select
      value={
        warehouseId
      }
      onChange={(
        event,
      ) =>
        setWarehouseId(
          event.target.value,
        )
      }
      disabled={
        warehousesLoading
      }
    >

      <option value="">
        جميع المستودعات
      </option>

      {warehouses.map(
        warehouse => (
          <option
            key={
              warehouse.id
            }
            value={
              warehouse.id
            }
          >
            {warehouse.nameAr ||
              warehouse.name}
            {" — "}
            {warehouse.code}
          </option>
        ),
      )}

    </select>

  </div>

)}

{activeInventoryTab ===
  "warehouse-stock" && (

  <div className="inventory-filter-field">

    <label>
      حالة الكمية
    </label>

    <select
      value={
        warehouseStockAvailability
      }
      onChange={(
        event,
      ) =>
        setWarehouseStockAvailability(
          event.target
            .value as
            | "all"
            | "in-stock"
            | "out-of-stock",
        )
      }
    >

      <option value="all">
        جميع الكميات
      </option>

      <option value="in-stock">
        متوفر
      </option>

      <option value="out-of-stock">
        صفر
      </option>

    </select>

  </div>

)}

            {/* =============================================
             * Transaction Filters
             * =========================================== */}

            {activeInventoryTab ===
              "transactions" && (
              <>

                <div className="inventory-filter-field">

                  <label>
                    نوع الحركة
                  </label>

                  <select
                    value={
                      type
                    }
                    onChange={(
                      event,
                    ) =>
                      setType(
                        event.target
                          .value as
                          InventoryTransactionType | "",
                      )
                    }
                  >

                    <option value="">
                      جميع الأنواع
                    </option>

                    <option value="set">
                      تعيين كمية
                    </option>

                    <option value="adjustment">
                      تعديل مخزون
                    </option>

                    <option value="reservation">
                      حجز مخزون
                    </option>

                    <option value="reservation_release">
                      تحرير حجز
                    </option>

<option value="reservation_consumed">
  استهلاك حجز
</option>

<option value="reservation_reallocation_out">
  نقل حجز من مستودع
</option>

<option value="reservation_reallocation_in">
  استلام حجز في مستودع
</option>
                    <option value="order_deduction">
                      خصم طلب
                    </option>

                    <option value="order_release">
                      إرجاع طلب
                    </option>

                    <option value="import">
                      استيراد
                    </option>

                    <option value="restore">
                      استعادة
                    </option>

                    <option value="bulk_adjustment">
                      تعديل جماعي
                    </option>

                  </select>

                </div>


                <div className="inventory-filter-field">

                  <label>
                    المصدر
                  </label>

                  <select
                    value={
                      source
                    }
                    onChange={(
                      event,
                    ) =>
                      setSource(
                        event.target
                          .value as
                          InventoryTransactionSource | "",
                      )
                    }
                  >

                    <option value="">
                      جميع المصادر
                    </option>

                    <option value="dashboard">
                      لوحة التحكم
                    </option>

                    <option value="api">
                      API
                    </option>

                    <option value="order">
                      طلب
                    </option>

                    <option value="import">
                      استيراد
                    </option>

                    <option value="bulk">
                      عملية جماعية
                    </option>

                    <option value="system">
                      النظام
                    </option>

                  </select>

                </div>


                <div className="inventory-filter-field">

                  <label>
                    Reservation ID
                  </label>

                  <input
                    type="text"
                    value={
                      reservationId
                    }
                    onChange={(
                      event,
                    ) =>
                      setReservationId(
                        event.target.value,
                      )
                    }
                    placeholder="معرف الحجز"
                  />

                </div>


                <div className="inventory-filter-field">

                  <label>
                    المنفذ
                  </label>

                  <input
                    type="text"
                    value={
                      performedBy
                    }
                    onChange={(
                      event,
                    ) =>
                      setPerformedBy(
                        event.target.value,
                      )
                    }
                    placeholder="UID"
                  />

                </div>

              </>
            )}


            {/* =============================================
             * Reservation Filters
             * =========================================== */}

            {activeInventoryTab ===
              "reservations" && (

              <div className="inventory-filter-field">

                <label>
                  حالة الحجز
                </label>

                <select
                  value={
                    reservationStatus
                  }
                  onChange={(
                    event,
                  ) =>
                    setReservationStatus(
                      event.target
                        .value as
                        InventoryReservationStatus | "",
                    )
                  }
                >

                  <option value="">
                    جميع الحالات
                  </option>

                  <option value="active">
                    نشط
                  </option>

                  <option value="removed">
  تمت إزالته
</option>

                  <option value="released">
                    محرر
                  </option>

                  <option value="consumed">
                    مستهلك
                  </option>

                  <option value="cancelled">
                    ملغي
                  </option>

                </select>

              </div>

            )}


            {/* =============================================
             * Common Filters
             * =========================================== */}

{activeInventoryTab !==
  "warehouse-stock" && (
  <>

            <div className="inventory-filter-field">

              <label>
                Order ID
              </label>

              <input
                type="text"
                value={
                  orderId
                }
                onChange={(
                  event,
                ) =>
                  setOrderId(
                    event.target.value,
                  )
                }
                placeholder="رقم الطلب"
              />

            </div>


            <div className="inventory-filter-field">

              <label>
                من تاريخ
              </label>

              <input
                type="date"
                value={
                  dateFrom
                }
                onChange={(
                  event,
                ) =>
                  setDateFrom(
                    event.target.value,
                  )
                }
              />

            </div>


            <div className="inventory-filter-field">

              <label>
                إلى تاريخ
              </label>

              <input
                type="date"
                value={
                  dateTo
                }
                onChange={(
                  event,
                ) =>
                  setDateTo(
                    event.target.value,
                  )
                }
              />

            </div>

  </>
)}
          </div>

        </div>


        {/* =================================================
         * Transactions
         * =============================================== */}

        {activeInventoryTab ===
          "transactions" && (
          <>

            <div className="inventory-summary">

              <div>
                <span>
                  الحركات المعروضة
                </span>

                <strong>
                  {summary.total}
                </strong>
              </div>


              <div>
                <span>
                  زيادات
                </span>

                <strong>
                  {summary.positive}
                </strong>
              </div>


              <div>
                <span>
                  خصومات
                </span>

                <strong>
                  {summary.negative}
                </strong>
              </div>


              <div>
                <span>
                  بدون تغيير
                </span>

                <strong>
                  {summary.zero}
                </strong>
              </div>

            </div>


            {loading ? (

              <div className="inventory-loading">
                جاري تحميل سجل المخزون...
              </div>

            ) : (

              <div className="inventory-table-wrapper">

                <table className="inventory-table">

                  <thead>

                    <tr>

                      <th>#</th>
                      <th>المنتج</th>
                      <th>الكود</th>
                      <th>المستودع</th>
                      <th>نوع الرصيد</th>
                      <th>النوع</th>
                      <th>قبل</th>
                      <th>التغيير</th>
                      <th>بعد</th>
                      <th>الحالة</th>
                      <th>المصدر</th>
                      <th>السبب</th>
                      <th>الطلب</th>
                      <th>الحجز</th>
                      <th>المنفذ</th>
                      <th>التاريخ</th>

                    </tr>

                  </thead>


                  <tbody>

                    {transactions.map(
                      (
                        transaction,
                        index,
                      ) => (

                        <tr
                          key={
                            transaction.transactionId
                          }
                          className="inventory-table-row"
                          onClick={() =>
                            handleOpenTransactionDetails(
                              transaction,
                            )
                          }
                        >

                          <td>
                            {index + 1}
                          </td>


                          <td className="inventory-product-name">

                            <FontAwesomeIcon
                              icon={
                                faBoxesStacked
                              }
                            />

                            <span>

                              {transaction.productName ||
                                "-"}

                            </span>

                          </td>


                          <td>
                            {transaction.productCode ||
                              "-"}
                          </td>


<td>
  <span className="inventory-warehouse-badge">
    <FontAwesomeIcon
      icon={
        faWarehouse
      }
    />

    {getWarehouseName(
      transaction.warehouseId,
    )}
  </span>
</td>

<td>
  {getTransactionBalanceTypeLabel(
    transaction,
  )}
</td>
                          <td>

                            <span className="inventory-type-badge">

                              {getTransactionTypeLabel(
                                transaction.type,
                              )}

                            </span>

                          </td>


                          <td>
                           {getDisplayedTransactionQuantities(
  transaction,
).previousQuantity}
                          </td>


                          <td>

  {(() => {
    const displayedQuantities =
      getDisplayedTransactionQuantities(
        transaction,
      );

    return (
      <span
        className={[
          "inventory-delta",

          displayedQuantities.delta >
          0
            ? "inventory-delta-positive"
            : displayedQuantities.delta <
                0
              ? "inventory-delta-negative"
              : "inventory-delta-zero",
        ].join(
          " ",
        )}
      >

        {displayedQuantities.delta >
        0
          ? `+${displayedQuantities.delta}`
          : displayedQuantities.delta}

      </span>
    );
  })()}

</td>


                          <td>

                            <strong>
                           {getDisplayedTransactionQuantities(
  transaction,
).newQuantity}
                            </strong>

                          </td>


                          <td>

                            <span
                              className={[
                                "inventory-stock-status",

                                `inventory-stock-status-${transaction.stockStatus}`,
                              ].join(
                                " ",
                              )}
                            >

                              {transaction.stockStatus ===
                              "available"
                                ? "متوفر"
                                : transaction.stockStatus ===
                                    "low_stock"
                                  ? "منخفض"
                                  : "نافد"}

                            </span>

                          </td>


                          <td>

                            {getTransactionSourceLabel(
                              transaction.source,
                            )}

                          </td>


                          <td className="inventory-reason-cell">

                            {transaction.reason ||
                              "-"}

                          </td>


                          <td>
                            {transaction.orderId ||
                              "-"}
                          </td>


                          <td>
                            {transaction.reservationId ||
                              "-"}
                          </td>


                          <td>

                            {transaction.performedByEmail ||
                              transaction.performedBy ||
                              "-"}

                          </td>


                          <td>

                            {formatInventoryDate(
                              transaction.createdAt,
                            )}

                          </td>

                        </tr>

                      ),
                    )}

                  </tbody>

                </table>


                {transactions.length ===
                  0 && (

                  <div className="inventory-empty">

                    لا توجد حركات مخزون مطابقة للفلاتر الحالية.

                  </div>

                )}

              </div>

            )}


            {hasMore && (

              <button
                type="button"
                className="inventory-load-more-btn"
                onClick={
                  handleLoadMoreTransactions
                }
                disabled={
                  loadingMore
                }
              >

                {loadingMore
                  ? "جاري التحميل..."
                  : "تحميل المزيد"}

              </button>

            )}

          </>
        )}


        {/* =================================================
         * Reservations
         * =============================================== */}

        {activeInventoryTab ===
          "reservations" && (
          <>

            {reservationsLoading ? (

              <div className="inventory-loading">
                جاري تحميل حجوزات المخزون...
              </div>

            ) : (

              <div className="inventory-table-wrapper">

                <table className="inventory-table">

                  <thead>

                    <tr>

                      <th>#</th>
                      <th>المنتج</th>
                      <th>الكود</th>
                      <th>المستودع</th>
                      <th>الكمية</th>
                      <th>الحالة</th>
                      <th>الطلب</th>
                      <th>السبب</th>
                      <th>حجز بواسطة</th>
                      <th>تاريخ الحجز</th>
                      <th>تاريخ الانتهاء</th>

                    </tr>

                  </thead>


                  <tbody>

                    {reservations.map(
                      (
                        reservation,
                        index,
                      ) => (

                        <tr
                          key={
                            reservation.reservationId
                          }
                          className="inventory-table-row"
                          onClick={() =>
                            handleOpenReservationDetails(
                              reservation,
                            )
                          }
                        >

                          <td>
                            {index + 1}
                          </td>


                          <td className="inventory-product-name">

                            <FontAwesomeIcon
                              icon={
                                faBoxesStacked
                              }
                            />

                            <span>

                              {reservation.productName ||
                                "-"}

                            </span>

                          </td>


                          <td>

                            {reservation.productCode ||
                              "-"}

                          </td>

                          <td>
  <span className="inventory-warehouse-badge">
    <FontAwesomeIcon
      icon={
        faWarehouse
      }
    />

    {getWarehouseName(
      reservation.warehouseId,
    )}
  </span>
</td>


                          <td>

                            <strong>
                              {reservation.quantity}
                            </strong>

                          </td>


                          <td>

                            <span
                              className={[
                                "inventory-reservation-status",

                                `inventory-reservation-status-${reservation.status}`,
                              ].join(
                                " ",
                              )}
                            >

                              {getReservationStatusLabel(
                                reservation.status,
                              )}

                            </span>

                          </td>


                          <td>

                            {reservation.orderId ||
                              "-"}

                          </td>


                          <td className="inventory-reason-cell">

                            {reservation.reason ||
                              "-"}

                          </td>


                          <td>

                            {reservation.reservedByEmail ||
                              reservation.reservedBy ||
                              "-"}

                          </td>


                          <td>

                            {formatInventoryDate(
                              reservation.reservedAt,
                            )}

                          </td>


                          <td>

                            {reservation.expiresAt
                              ? formatInventoryDate(
                                  reservation.expiresAt,
                                )
                              : "-"}

                          </td>

                        </tr>

                      ),
                    )}

                  </tbody>

                </table>


                {reservations.length ===
                  0 && (

                  <div className="inventory-empty">

                    لا توجد حجوزات مخزون مطابقة للفلاتر الحالية.

                  </div>

                )}

              </div>

            )}


            {reservationsHasMore && (

              <button
                type="button"
                className="inventory-load-more-btn"
                onClick={
                  handleLoadMoreReservations
                }
                disabled={
                  reservationsLoadingMore
                }
              >

                {reservationsLoadingMore
                  ? "جاري التحميل..."
                  : "تحميل المزيد"}

              </button>

            )}

          </>
        )}


{/* =================================================
 * Warehouse Stock
 * =============================================== */}

{activeInventoryTab ===
  "warehouse-stock" && (
  <>

    <div className="inventory-warehouse-note">

      <FontAwesomeIcon
        icon={
          faWarehouse
        }
      />

      <div>
        <strong>
          مخزون المستودعات
        </strong>

        <span>
          هذه الشاشة تقرأ مجموعة warehouseStocks.
          قبل تنفيذ Migration قد تكون القائمة فارغة، وهذا لا يعني أن Product.quantity يساوي صفر.
        </span>
      </div>

    </div>


    <div className="inventory-summary">

      <div>
        <span>
          السجلات المعروضة
        </span>

        <strong>
          {
            warehouseStocks
              .length
          }
        </strong>
      </div>


      <div>
        <span>
          المتاح
        </span>

        <strong className="inventory-details-positive">
          {
            warehouseStockSummary
              .available
          }
        </strong>
      </div>


      <div>
        <span>
          المحجوز
        </span>

        <strong>
          {
            warehouseStockSummary
              .reserved
          }
        </strong>
      </div>


      <div>
        <span>
          الفعلي On Hand
        </span>

        <strong>
          {
            warehouseStockSummary
              .onHand
          }
        </strong>
      </div>

    </div>


    {warehouseStocksLoading ? (

      <div className="inventory-loading">
        جاري تحميل مخزون المستودعات...
      </div>

    ) : (

      <div className="inventory-table-wrapper">

        <table className="inventory-table inventory-warehouse-table">

          <thead>
            <tr>

              <th>#</th>

              <th>
                المستودع
              </th>

              <th>
                Product ID
              </th>

              <th>
                المتاح
              </th>

              <th>
                المحجوز
              </th>

              <th>
                On Hand
              </th>

              <th>
                آخر تحديث
              </th>

            </tr>
          </thead>


          <tbody>

            {warehouseStocks.map(
              (
                stock,
                index,
              ) => (

                <tr
                  key={
                    stock.stockId
                  }
                >

                  <td>
                    {index + 1}
                  </td>


                  <td>
                    <span className="inventory-warehouse-badge">

                      <FontAwesomeIcon
                        icon={
                          faWarehouse
                        }
                      />

                      {getWarehouseName(
                        stock.warehouseId,
                      )}

                    </span>
                  </td>


                  <td>
                    <strong>
                      {stock.productId}
                    </strong>

                    {selectedProduct &&
                      selectedProduct.id ===
                        stock.productId && (
                        <div className="inventory-warehouse-product-name">
                          {selectedProduct.name ||
                            selectedProduct.code}
                        </div>
                      )}
                  </td>


                  <td>
                    <span className="inventory-stock-number inventory-stock-number-available">
                      {stock.quantity}
                    </span>
                  </td>


                  <td>
                    <span className="inventory-stock-number inventory-stock-number-reserved">
                      {stock.reservedQuantity}
                    </span>
                  </td>


                  <td>
                    <strong className="inventory-stock-number inventory-stock-number-on-hand">
                      {stock.onHandQuantity}
                    </strong>
                  </td>


                  <td>
                    {formatInventoryDate(
                      stock.updatedAt,
                    )}
                  </td>

                </tr>

              ),
            )}

          </tbody>

        </table>


        {warehouseStocks.length ===
          0 && (

          <div className="inventory-empty">

            لا توجد سجلات Warehouse Stock مطابقة للفلاتر الحالية.

          </div>

        )}

      </div>

    )}


    {warehouseStocksHasMore && (

      <button
        type="button"
        className="inventory-load-more-btn"
        onClick={
          handleLoadMoreWarehouseStocks
        }
        disabled={
          warehouseStocksLoadingMore
        }
      >

        {warehouseStocksLoadingMore
          ? "جاري التحميل..."
          : "تحميل المزيد"}

      </button>

    )}

  </>
)}

        {/* =================================================
         * Create Inventory Reservation Modal
         * =============================================== */}

        <Modal
          open={openCreateReservationModal}
          onClose={handleCloseCreateReservationModal}
        >
          <Box className="inventory-details-modal">

            <div className="inventory-details-header">
              <div>
                <h2>إنشاء حجز مخزون</h2>
                <span>
                  {selectedProduct
                    ? `${selectedProduct.name || "-"} — ${selectedProduct.code || selectedProduct.id}`
                    : "-"}
                </span>
              </div>

              <button
                type="button"
                onClick={
                  handleCloseCreateReservationModal
                }
                disabled={reservationCreating}
              >
                ×
              </button>
            </div>

            <div className="inventory-details-content">
              <div className="inventory-details-section">

                <h3>بيانات الحجز</h3>

                <div className="inventory-reallocate-form">

                  <div className="inventory-reallocate-grid">

                    <label>
                      <span>المستودع</span>
                      <select
                        value={reservationWarehouseId}
                        onChange={event => {
                          setReservationWarehouseId(
                            event.target.value,
                          );
                          setReservationWarehouseStock(null);
                          setReservationQuantity("");
                        }}
                        disabled={
                          reservationCreating ||
                          warehousesLoading
                        }
                      >
                        <option value="">
                          اختر المستودع
                        </option>

                        {warehouses
                          .filter(
                            warehouse =>
                              warehouse.isActive,
                          )
                          .map(
                            warehouse => (
                              <option
                                key={warehouse.id}
                                value={warehouse.id}
                              >
                                {warehouse.nameAr ||
                                  warehouse.name}
                                {" — "}
                                {warehouse.code}
                                {warehouse.isDefault
                                  ? " — افتراضي"
                                  : ""}
                              </option>
                            ),
                          )}
                      </select>
                    </label>

                    <label>
                      <span>الكمية المراد حجزها</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={reservationQuantity}
                        onChange={event =>
                          setReservationQuantity(
                            event.target.value,
                          )
                        }
                        placeholder={
                          reservationWarehouseStockLoading
                            ? "جاري تحميل المخزون..."
                            : reservationWarehouseStock
                              ? `المتاح: ${reservationCurrentAvailable}`
                              : "اختر المستودع"
                        }
                        disabled={
                          reservationCreating ||
                          reservationWarehouseStockLoading ||
                          !reservationWarehouseStock
                        }
                      />
                    </label>

                    <label>
                      <span>
                        تاريخ انتهاء الحجز — اختياري
                      </span>
                      <input
                        type="datetime-local"
                        value={reservationExpiresAt}
                        onChange={event =>
                          setReservationExpiresAt(
                            event.target.value,
                          )
                        }
                        disabled={reservationCreating}
                      />
                    </label>

                  </div>

                  {reservationWarehouseStockLoading ? (
                    <div className="inventory-warehouse-feature-warning">
                      جاري تحميل رصيد المنتج في المستودع...
                    </div>
                  ) : reservationWarehouseStock ? (
                    <>
                      <div className="inventory-details-grid">

                        <div className="inventory-details-item">
                          <span>المتاح حاليًا</span>
                          <strong>
                            {reservationCurrentAvailable}
                          </strong>
                        </div>

                        <div className="inventory-details-item">
                          <span>المحجوز حاليًا</span>
                          <strong>
                            {reservationCurrentReserved}
                          </strong>
                        </div>

                        <div className="inventory-details-item">
                          <span>المخزون الفعلي</span>
                          <strong>
                            {reservationCurrentOnHand}
                          </strong>
                        </div>

                      </div>

                      {reservationAvailableAfter !== null &&
                        reservationReservedAfter !== null && (
                          <div className="inventory-details-grid">

                            <div className="inventory-details-item">
                              <span>
                                المتاح بعد الحجز
                              </span>
                              <strong>
                                {reservationAvailableAfter}
                              </strong>
                            </div>

                            <div className="inventory-details-item">
                              <span>
                                المحجوز بعد الحجز
                              </span>
                              <strong>
                                {reservationReservedAfter}
                              </strong>
                            </div>

                            <div className="inventory-details-item">
                              <span>
                                المخزون الفعلي بعد الحجز
                              </span>
                              <strong>
                                {reservationCurrentOnHand}
                              </strong>
                            </div>

                          </div>
                        )}

                      {reservationAvailableAfter !== null &&
                        reservationAvailableAfter < 0 && (
                          <div className="inventory-warehouse-feature-warning">
                            الكمية المطلوبة أكبر من الكمية المتاحة في المستودع.
                          </div>
                        )}
                    </>
                  ) : reservationWarehouseId ? (
                    <div className="inventory-warehouse-feature-warning">
                      لا توجد بيانات مخزون متاحة لهذا المنتج في المستودع المحدد.
                    </div>
                  ) : null}

                  <label>
                    <span>سبب الحجز</span>
                    <textarea
                      value={reservationReason}
                      onChange={event =>
                        setReservationReason(
                          event.target.value,
                        )
                      }
                      maxLength={500}
                      placeholder="مثال: حجز إداري مؤقت لاختبار دورة المخزون"
                      disabled={reservationCreating}
                    />
                  </label>

                  <div className="inventory-warehouse-feature-warning">
                    إنشاء الحجز ينقل الكمية من المتاح إلى المحجوز داخل نفس المستودع، لذلك لا تتغير الكمية الفعلية للمخزون.
                  </div>

                  <div className="inventory-reallocate-actions">
                    <button
                      type="button"
                      onClick={
                        handleCreateReservation
                      }
                      disabled={
                        reservationCreating ||
                        reservationWarehouseStockLoading ||
                        !reservationWarehouseId ||
                        !reservationWarehouseStock ||
                        !reservationQuantity ||
                        !reservationReason.trim() ||
                        reservationAvailableAfter === null ||
                        reservationAvailableAfter < 0
                      }
                    >
                      {reservationCreating
                        ? "جاري إنشاء الحجز..."
                        : "إنشاء الحجز"}
                    </button>
                  </div>

                </div>
              </div>
            </div>

          </Box>
        </Modal>


        {/* =================================================
         * Physical Stock Count Reconciliation Modal
         * =============================================== */}

        <Modal
          open={openAdjustmentModal}
          onClose={handleCloseAdjustmentModal}
        >
          <Box className="inventory-details-modal">

            <div className="inventory-details-header">
              <div>
                <h2>تسوية المخزون بعد الجرد</h2>
                <span>
                  {selectedProduct
                    ? `${selectedProduct.name || "-"} — ${selectedProduct.code || selectedProduct.id}`
                    : "-"}
                </span>
              </div>

              <button
                type="button"
                onClick={handleCloseAdjustmentModal}
                disabled={adjustmentProcessing}
              >
                ×
              </button>
            </div>

            <div className="inventory-details-content">
              <div className="inventory-details-section">

                <h3>تفاصيل الجرد</h3>

                <div className="inventory-reallocate-form">

                  <div className="inventory-reallocate-grid">

                    <label>
                      <span>المستودع</span>
                      <select
                        value={adjustmentWarehouseId}
                        onChange={event => {
                          setAdjustmentWarehouseId(
                            event.target.value,
                          );
                          setAdjustmentWarehouseStock(null);
                          setAdjustmentQuantity("");
                        }}
                        disabled={
                          adjustmentProcessing ||
                          warehousesLoading
                        }
                      >
                        <option value="">
                          اختر المستودع
                        </option>

                        {warehouses
                          .filter(
                            warehouse =>
                              warehouse.isActive,
                          )
                          .map(
                            warehouse => (
                              <option
                                key={warehouse.id}
                                value={warehouse.id}
                              >
                                {warehouse.nameAr ||
                                  warehouse.name}
                                {" — "}
                                {warehouse.code}
                                {warehouse.isDefault
                                  ? " — افتراضي"
                                  : ""}
                              </option>
                            ),
                          )}
                      </select>
                    </label>

                    <label>
                      <span>
                        الكمية الفعلية بعد الجرد
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={adjustmentQuantity}
                        onChange={event =>
                          setAdjustmentQuantity(
                            event.target.value,
                          )
                        }
                        placeholder={
                          adjustmentWarehouseStockLoading
                            ? "جاري تحميل المخزون..."
                            : adjustmentWarehouseStock
                              ? `المخزون الفعلي الحالي: ${adjustmentCurrentOnHand}`
                              : "اختر المستودع"
                        }
                        disabled={
                          adjustmentProcessing ||
                          adjustmentWarehouseStockLoading ||
                          !adjustmentWarehouseStock
                        }
                      />
                    </label>

                    <label>
                      <span>
                        Order ID — اختياري
                      </span>
                      <input
                        type="text"
                        value={adjustmentOrderId}
                        onChange={event =>
                          setAdjustmentOrderId(
                            event.target.value,
                          )
                        }
                        placeholder="رقم الطلب إن وجد"
                        disabled={adjustmentProcessing}
                      />
                    </label>

                  </div>

                  {adjustmentWarehouseStockLoading ? (
                    <div className="inventory-warehouse-feature-warning">
                      جاري تحميل الرصيد الحالي للمنتج في المستودع...
                    </div>
                  ) : adjustmentWarehouseStock ? (
                    <>
                      <div className="inventory-details-grid">
                        <div className="inventory-details-item">
                          <span>المتاح حاليًا</span>
                          <strong>
                            {adjustmentCurrentAvailable}
                          </strong>
                        </div>

                        <div className="inventory-details-item">
                          <span>المحجوز حاليًا</span>
                          <strong>
                            {adjustmentCurrentReserved}
                          </strong>
                        </div>

                        <div className="inventory-details-item">
                          <span>
                            المخزون الفعلي الحالي
                          </span>
                          <strong>
                            {adjustmentCurrentOnHand}
                          </strong>
                        </div>
                      </div>

                      {adjustmentDelta !== null &&
                        adjustmentNewAvailable !== null &&
                        adjustmentNewOnHand !== null && (
                          <div className="inventory-details-grid">
                            <div className="inventory-details-item">
                              <span>فرق الجرد</span>
                              <strong>
                                {adjustmentDelta > 0
                                  ? `+${adjustmentDelta}`
                                  : adjustmentDelta}
                              </strong>
                            </div>

                            <div className="inventory-details-item">
                              <span>
                                المتاح بعد التسوية
                              </span>
                              <strong>
                                {adjustmentNewAvailable}
                              </strong>
                            </div>

                            <div className="inventory-details-item">
                              <span>
                                المحجوز بعد التسوية
                              </span>
                              <strong>
                                {adjustmentCurrentReserved}
                              </strong>
                            </div>

                            <div className="inventory-details-item">
                              <span>
                                المخزون الفعلي بعد التسوية
                              </span>
                              <strong>
                                {adjustmentNewOnHand}
                              </strong>
                            </div>
                          </div>
                        )}

                      {adjustmentNewAvailable !== null &&
                        adjustmentNewAvailable < 0 && (
                          <div className="inventory-warehouse-feature-warning">
                            لا يمكن تنفيذ التسوية لأن الكمية الفعلية المدخلة أقل من الكمية المحجوزة الحالية ({adjustmentCurrentReserved}). عالج الحجوزات أولًا.
                          </div>
                        )}
                    </>
                  ) : adjustmentWarehouseId ? (
                    <div className="inventory-warehouse-feature-warning">
                      لا توجد بيانات مخزون متاحة لهذا المنتج في المستودع المحدد.
                    </div>
                  ) : null}

                  <label>
                    <span>
                      Reference ID — اختياري
                    </span>
                    <input
                      type="text"
                      value={adjustmentReferenceId}
                      onChange={event =>
                        setAdjustmentReferenceId(
                          event.target.value,
                        )
                      }
                      placeholder="مرجع داخلي للعملية"
                      disabled={adjustmentProcessing}
                    />
                  </label>

                  <label>
                    <span>سبب التسوية</span>
                    <textarea
                      value={adjustmentReason}
                      onChange={event =>
                        setAdjustmentReason(
                          event.target.value,
                        )
                      }
                      maxLength={500}
                      placeholder="مثال: نتيجة الجرد الفعلي للمستودع"
                      disabled={adjustmentProcessing}
                    />
                  </label>

                  <div className="inventory-warehouse-feature-warning">
                    المخزون الفعلي = المتاح + المحجوز. تسوية الجرد تعدّل المتاح بفرق الجرد فقط، بينما تبقى الكمية المحجوزة كما هي.
                  </div>

                  <div className="inventory-reallocate-actions">
                    <button
                      type="button"
                      onClick={handleCreateAdjustment}
                      disabled={
                        adjustmentProcessing ||
                        adjustmentWarehouseStockLoading ||
                        !adjustmentWarehouseId ||
                        !adjustmentWarehouseStock ||
                        !adjustmentQuantity ||
                        !adjustmentReason.trim() ||
                        adjustmentDelta === null ||
                        adjustmentDelta === 0 ||
                        adjustmentNewAvailable === null ||
                        adjustmentNewAvailable < 0
                      }
                    >
                      {adjustmentProcessing
                        ? "جاري تنفيذ التسوية..."
                        : "تنفيذ تسوية الجرد"}
                    </button>
                  </div>

                </div>
              </div>
            </div>

          </Box>
        </Modal>


        {/* =================================================
         * Manual Inventory Adjustment Modal
         * =============================================== */}

        <Modal
          open={openManualAdjustmentModal}
          onClose={handleCloseManualAdjustmentModal}
        >
          <Box className="inventory-details-modal">

            <div className="inventory-details-header">
              <div>
                <h2>تعديل يدوي للمخزون</h2>
                <span>
                  {selectedProduct
                    ? `${selectedProduct.name || "-"} — ${selectedProduct.code || selectedProduct.id}`
                    : "-"}
                </span>
              </div>

              <button
                type="button"
                onClick={handleCloseManualAdjustmentModal}
                disabled={manualAdjustmentProcessing}
              >
                ×
              </button>
            </div>

            <div className="inventory-details-content">
              <div className="inventory-details-section">

                <h3>تفاصيل التعديل اليدوي</h3>

                <div className="inventory-reallocate-form">

                  <div className="inventory-reallocate-grid">

                    <label>
                      <span>المستودع</span>
                      <select
                        value={manualAdjustmentWarehouseId}
                        onChange={event => {
                          setManualAdjustmentWarehouseId(
                            event.target.value,
                          );
                          setManualAdjustmentWarehouseStock(
                            null,
                          );
                        }}
                        disabled={
                          manualAdjustmentProcessing ||
                          warehousesLoading
                        }
                      >
                        <option value="">
                          اختر المستودع
                        </option>

                        {warehouses
                          .filter(
                            warehouse =>
                              warehouse.isActive,
                          )
                          .map(
                            warehouse => (
                              <option
                                key={warehouse.id}
                                value={warehouse.id}
                              >
                                {warehouse.nameAr ||
                                  warehouse.name}
                                {" — "}
                                {warehouse.code}
                                {warehouse.isDefault
                                  ? " — افتراضي"
                                  : ""}
                              </option>
                            ),
                          )}
                      </select>
                    </label>

                    <label>
                      <span>نوع العملية</span>
                      <select
                        value={manualAdjustmentMode}
                        onChange={event =>
                          setManualAdjustmentMode(
                            event.target.value as ManualAdjustmentMode,
                          )
                        }
                        disabled={manualAdjustmentProcessing}
                      >
                        <option value="increase">
                          إضافة كمية
                        </option>
                        <option value="decrease">
                          خصم كمية
                        </option>
                      </select>
                    </label>

                    <label>
                      <span>الكمية</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={manualAdjustmentQuantity}
                        onChange={event =>
                          setManualAdjustmentQuantity(
                            event.target.value,
                          )
                        }
                        placeholder="أدخل الكمية"
                        disabled={
                          manualAdjustmentProcessing ||
                          manualAdjustmentWarehouseStockLoading ||
                          !manualAdjustmentWarehouseStock
                        }
                      />
                    </label>

                    <label>
                      <span>
                        Order ID — اختياري
                      </span>
                      <input
                        type="text"
                        value={manualAdjustmentOrderId}
                        onChange={event =>
                          setManualAdjustmentOrderId(
                            event.target.value,
                          )
                        }
                        placeholder="رقم الطلب إن وجد"
                        disabled={manualAdjustmentProcessing}
                      />
                    </label>

                  </div>

                  {manualAdjustmentWarehouseStockLoading ? (
                    <div className="inventory-warehouse-feature-warning">
                      جاري تحميل الرصيد الحالي للمنتج في المستودع...
                    </div>
                  ) : manualAdjustmentWarehouseStock ? (
                    <>
                      <div className="inventory-details-grid">
                        <div className="inventory-details-item">
                          <span>المتاح حاليًا</span>
                          <strong>
                            {manualAdjustmentCurrentAvailable}
                          </strong>
                        </div>

                        <div className="inventory-details-item">
                          <span>المحجوز حاليًا</span>
                          <strong>
                            {manualAdjustmentCurrentReserved}
                          </strong>
                        </div>

                        <div className="inventory-details-item">
                          <span>
                            المخزون الفعلي الحالي
                          </span>
                          <strong>
                            {manualAdjustmentCurrentOnHand}
                          </strong>
                        </div>
                      </div>

                      {manualAdjustmentDelta !== null &&
                        manualAdjustmentNewAvailable !== null &&
                        manualAdjustmentNewOnHand !== null && (
                          <div className="inventory-details-grid">
                            <div className="inventory-details-item">
                              <span>التغيير</span>
                              <strong>
                                {manualAdjustmentDelta > 0
                                  ? `+${manualAdjustmentDelta}`
                                  : manualAdjustmentDelta}
                              </strong>
                            </div>

                            <div className="inventory-details-item">
                              <span>
                                المتاح بعد العملية
                              </span>
                              <strong>
                                {manualAdjustmentNewAvailable}
                              </strong>
                            </div>

                            <div className="inventory-details-item">
                              <span>
                                المحجوز بعد العملية
                              </span>
                              <strong>
                                {manualAdjustmentCurrentReserved}
                              </strong>
                            </div>

                            <div className="inventory-details-item">
                              <span>
                                المخزون الفعلي بعد العملية
                              </span>
                              <strong>
                                {manualAdjustmentNewOnHand}
                              </strong>
                            </div>
                          </div>
                        )}

                      {manualAdjustmentNewAvailable !== null &&
                        manualAdjustmentNewAvailable < 0 && (
                          <div className="inventory-warehouse-feature-warning">
                            لا يمكن تنفيذ الخصم لأن الكمية المتاحة ستصبح سالبة. المتاح حاليًا هو {manualAdjustmentCurrentAvailable} وحدة.
                          </div>
                        )}
                    </>
                  ) : manualAdjustmentWarehouseId ? (
                    <div className="inventory-warehouse-feature-warning">
                      لا توجد بيانات مخزون متاحة لهذا المنتج في المستودع المحدد.
                    </div>
                  ) : null}

                  <label>
                    <span>
                      Reference ID — اختياري
                    </span>
                    <input
                      type="text"
                      value={manualAdjustmentReferenceId}
                      onChange={event =>
                        setManualAdjustmentReferenceId(
                          event.target.value,
                        )
                      }
                      placeholder="مرجع داخلي للعملية"
                      disabled={manualAdjustmentProcessing}
                    />
                  </label>

                  <label>
                    <span>
                      سبب التعديل اليدوي
                    </span>
                    <textarea
                      value={manualAdjustmentReason}
                      onChange={event =>
                        setManualAdjustmentReason(
                          event.target.value,
                        )
                      }
                      maxLength={500}
                      placeholder="مثال: تصحيح تشغيلي، بضاعة تالفة، أو إضافة استثنائية"
                      disabled={manualAdjustmentProcessing}
                    />
                  </label>

                  <div className="inventory-warehouse-feature-warning">
                    التعديل اليدوي مستقل عن الجرد. استخدمه لإضافة أو خصم كمية محددة مباشرة. الكمية المحجوزة لا تتغير.
                  </div>

                  <div className="inventory-reallocate-actions">
                    <button
                      type="button"
                      onClick={handleCreateManualAdjustment}
                      disabled={
                        manualAdjustmentProcessing ||
                        manualAdjustmentWarehouseStockLoading ||
                        !manualAdjustmentWarehouseId ||
                        !manualAdjustmentWarehouseStock ||
                        !manualAdjustmentQuantity ||
                        !manualAdjustmentReason.trim() ||
                        manualAdjustmentDelta === null ||
                        manualAdjustmentNewAvailable === null ||
                        manualAdjustmentNewAvailable < 0
                      }
                    >
                      {manualAdjustmentProcessing
                        ? "جاري تنفيذ التعديل..."
                        : manualAdjustmentMode === "increase"
                          ? "إضافة الكمية"
                          : "خصم الكمية"}
                    </button>
                  </div>

                </div>
              </div>
            </div>

          </Box>
        </Modal>


        {/* =================================================
         * Transaction Details Modal
         * =============================================== */}

        <Modal
          open={
            openTransactionModal
          }
          onClose={
            handleCloseTransactionDetails
          }
        >

          <Box className="inventory-details-modal">

            <div className="inventory-details-header">

              <div>

                <h2>
                  تفاصيل حركة المخزون
                </h2>

                <span>

                  {selectedTransaction
                    ?.transactionId ||
                    "-"}

                </span>

              </div>


              <button
                type="button"
                onClick={
                  handleCloseTransactionDetails
                }
              >
                ×
              </button>

            </div>


            {selectedTransaction && (

              <div className="inventory-details-content">


                <div className="inventory-details-section">

                  <h3>
                    المنتج
                  </h3>

                  <div className="inventory-details-grid">

                    <div>

                      <span>
                        اسم المنتج
                      </span>

                      <strong>

                        {selectedTransaction.productName ||
                          "-"}

                      </strong>

                    </div>


                    <div>

                      <span>
                        كود المنتج
                      </span>

                      <strong>

                        {selectedTransaction.productCode ||
                          "-"}

                      </strong>

                    </div>


                    <div>

                      <span>
                        Product ID
                      </span>

                      <strong>

                        {selectedTransaction.productId ||
                          "-"}

                      </strong>

                    </div>

                  </div>

                </div>


                <div className="inventory-details-section">

                  <h3>
                    حركة المخزون
                  </h3>

                  <div className="inventory-details-grid">

                    <div>

                      <span>
                        نوع الحركة
                      </span>

                      <strong>

                        {getTransactionTypeLabel(
                          selectedTransaction.type,
                        )}

                      </strong>

                    </div>

<div>

  <span>
    المستودع
  </span>

  <strong>
    {getWarehouseName(
      selectedTransaction
        .warehouseId,
    )}
  </strong>

</div>

<div>

  <span>
    نوع الرصيد
  </span>

  <strong>
    {getTransactionBalanceTypeLabel(
      selectedTransaction,
    )}
  </strong>

</div>

                  {(() => {
  const displayedQuantities =
    getDisplayedTransactionQuantities(
      selectedTransaction,
    );

  return (
    <>
      <div>

        <span>
          الكمية قبل
        </span>

        <strong>
          {
            displayedQuantities
              .previousQuantity
          }
        </strong>

      </div>


      <div>

        <span>
          التغيير
        </span>

        <strong
          className={
            displayedQuantities.delta >
            0
              ? "inventory-details-positive"
              : displayedQuantities.delta <
                  0
                ? "inventory-details-negative"
                : ""
          }
        >

          {displayedQuantities.delta >
          0
            ? `+${displayedQuantities.delta}`
            : displayedQuantities.delta}

        </strong>

      </div>


      <div>

        <span>
          الكمية بعد
        </span>

        <strong>
          {
            displayedQuantities
              .newQuantity
          }
        </strong>

      </div>
    </>
  );
})()}


                    <div>

                      <span>
                        حالة المخزون
                      </span>

                      <strong>

                        {selectedTransaction.stockStatus ===
                        "available"
                          ? "متوفر"
                          : selectedTransaction.stockStatus ===
                              "low_stock"
                            ? "منخفض"
                            : "نافد"}

                      </strong>

                    </div>


                    <div>

                      <span>
                        المصدر
                      </span>

                      <strong>

                        {getTransactionSourceLabel(
                          selectedTransaction.source,
                        )}

                      </strong>

                    </div>

                  </div>

                </div>


                <div className="inventory-details-section">

                  <h3>
                    المراجع
                  </h3>

                  <div className="inventory-details-grid">

                    <div>

                      <span>
                        Order ID
                      </span>

                      <strong>

                        {selectedTransaction.orderId ||
                          "-"}

                      </strong>

                    </div>


                    <div>

                      <span>
                        Reservation ID
                      </span>

                      <strong>

                        {selectedTransaction.reservationId ||
                          "-"}

                      </strong>

                    </div>


                    <div>

                      <span>
                        Reference ID
                      </span>

                      <strong>

                        {selectedTransaction.referenceId ||
                          "-"}

                      </strong>

                    </div>

                  </div>

                </div>


                <div className="inventory-details-section">

                  <h3>
                    التنفيذ
                  </h3>

                  <div className="inventory-details-grid">

                    <div>

                      <span>
                        المنفذ
                      </span>

                      <strong>

                        {selectedTransaction.performedBy ||
                          "-"}

                      </strong>

                    </div>


                    <div>

                      <span>
                        البريد الإلكتروني
                      </span>

                      <strong>

                        {selectedTransaction.performedByEmail ||
                          "-"}

                      </strong>

                    </div>


                    <div>

                      <span>
                        التاريخ
                      </span>

                      <strong>

                        {formatInventoryDate(
                          selectedTransaction.createdAt,
                        )}

                      </strong>

                    </div>

                  </div>

                </div>


                <div className="inventory-details-section">

                  <h3>
                    سبب العملية
                  </h3>

                  <div className="inventory-details-reason">

                    {selectedTransaction.reason ||
                      "لم يتم تسجيل سبب."}

                  </div>

                </div>

              </div>

            )}

          </Box>

        </Modal>


        {/* =================================================
         * Reservation Details Modal
         * =============================================== */}

        <Modal
          open={
            openReservationModal
          }
          onClose={
            handleCloseReservationDetails
          }
        >

          <Box className="inventory-details-modal">

            <div className="inventory-details-header">

              <div>

                <h2>
                  تفاصيل حجز المخزون
                </h2>

                <span>

                  {selectedReservation
                    ?.reservationId ||
                    "-"}

                </span>

              </div>


              <button
                type="button"
                onClick={
                  handleCloseReservationDetails
                }
                disabled={
                  releasingReservation ||
                  reallocatingReservation
                }
              >
                ×
              </button>

            </div>


            {selectedReservation && (

              <div className="inventory-details-content">


                {/* =========================================
                 * Product
                 * ======================================= */}

                <div className="inventory-details-section">

                  <h3>
                    المنتج
                  </h3>

                  <div className="inventory-details-grid">

                    <div>

                      <span>
                        اسم المنتج
                      </span>

                      <strong>

                        {selectedReservation.productName ||
                          "-"}

                      </strong>

                    </div>


                    <div>

                      <span>
                        كود المنتج
                      </span>

                      <strong>

                        {selectedReservation.productCode ||
                          "-"}

                      </strong>

                    </div>


                    <div>

                      <span>
                        Product ID
                      </span>

                      <strong>

                        {selectedReservation.productId ||
                          "-"}

                      </strong>

                    </div>


                    <div>

                      <span>
                        المستودع
                      </span>

                      <strong>

                        {getWarehouseName(
                          selectedReservation
                            .warehouseId,
                        )}

                      </strong>

                    </div>


                    <div>

                      <span>
                        الكمية المحجوزة
                      </span>

                      <strong>

                        {selectedReservation.quantity}

                      </strong>

                    </div>


                    <div>

                      <span>
                        الحالة
                      </span>

                      <strong>

                        {getReservationStatusLabel(
                          selectedReservation.status,
                        )}

                      </strong>

                    </div>


                    <div>

                      <span>
                        Order ID
                      </span>

                      <strong>

                        {selectedReservation.orderId ||
                          "-"}

                      </strong>

                    </div>

                  </div>

                </div>


                {/* =========================================
                 * Reservation
                 * ======================================= */}

                <div className="inventory-details-section">

                  <h3>
                    معلومات الحجز
                  </h3>

                  <div className="inventory-details-grid">

                    <div>

                      <span>
                        Reservation ID
                      </span>

                      <strong>

                        {selectedReservation.reservationId}

                      </strong>

                    </div>


                    <div>

                      <span>
                        Transaction ID
                      </span>

                      <strong>

                        {selectedReservation.transactionId ||
                          "-"}

                      </strong>

                    </div>


                    <div>

                      <span>
                        Idempotency Key
                      </span>

                      <strong>

                        {selectedReservation.idempotencyKey ||
                          "-"}

                      </strong>

                    </div>


                    <div>

                      <span>
                        تاريخ الحجز
                      </span>

                      <strong>

                        {formatInventoryDate(
                          selectedReservation.reservedAt,
                        )}

                      </strong>

                    </div>


                    <div>

                      <span>
                        تاريخ الانتهاء
                      </span>

                      <strong>

                        {selectedReservation.expiresAt
                          ? formatInventoryDate(
                              selectedReservation.expiresAt,
                            )
                          : "-"}

                      </strong>

                    </div>


                    <div>

                      <span>
                        آخر تحديث
                      </span>

                      <strong>

                        {formatInventoryDate(
                          selectedReservation.updatedAt,
                        )}

                      </strong>

                    </div>

                  </div>

                </div>


                {/* =========================================
                 * Reserved By
                 * ======================================= */}

                <div className="inventory-details-section">

                  <h3>
                    منفذ الحجز
                  </h3>

                  <div className="inventory-details-grid">

                    <div>

                      <span>
                        UID
                      </span>

                      <strong>

                        {selectedReservation.reservedBy ||
                          "-"}

                      </strong>

                    </div>


                    <div>

                      <span>
                        البريد الإلكتروني
                      </span>

                      <strong>

                        {selectedReservation.reservedByEmail ||
                          "-"}

                      </strong>

                    </div>

                  </div>

                </div>


                {/* =========================================
                 * Reason
                 * ======================================= */}

                <div className="inventory-details-section">

                  <h3>
                    سبب الحجز
                  </h3>

                  <div className="inventory-details-reason">

                    {selectedReservation.reason ||
                      "لم يتم تسجيل سبب للحجز."}

                  </div>

                </div>


                {/* =========================================
                 * Release Details
                 * ======================================= */}

                {selectedReservation.status ===
                  "released" && (

                  <div className="inventory-details-section">

                    <h3>
                      معلومات تحرير الحجز
                    </h3>

                    <div className="inventory-details-grid">

                      <div>

                        <span>
                          حرره
                        </span>

                        <strong>

                          {selectedReservation.releasedBy ||
                            "-"}

                        </strong>

                      </div>


                      <div>

                        <span>
                          البريد الإلكتروني
                        </span>

                        <strong>

                          {selectedReservation.releasedByEmail ||
                            "-"}

                        </strong>

                      </div>


                      <div>

                        <span>
                          تاريخ التحرير
                        </span>

                        <strong>

                          {selectedReservation.releasedAt
                            ? formatInventoryDate(
                                selectedReservation.releasedAt,
                              )
                            : "-"}

                        </strong>

                      </div>


                      <div>

                        <span>
                          Release Transaction ID
                        </span>

                        <strong>

                          {selectedReservation.releaseTransactionId ||
                            "-"}

                        </strong>

                      </div>

                    </div>


                    <div
                      className="inventory-details-reason"
                      style={{
                        marginTop:
                          "12px",
                      }}
                    >

                      {selectedReservation.releaseReason ||
                        "لم يتم تسجيل سبب التحرير."}

                    </div>

                  </div>

                )}

{/* =========================================
 * Reallocate Reservation
 * ======================================= */}

{selectedReservation.status ===
  "active" &&
  !selectedReservation
    .warehouseId && (

  <div className="inventory-details-section">

    <h3>
      إعادة توزيع الحجز
    </h3>

    <div className="inventory-release-warning">
      هذا حجز قديم غير مرتبط بمستودع، لذلك لا يمكن إعادة توزيعه بين المستودعات.
    </div>

  </div>

)}


{selectedReservation.status ===
  "active" &&
  selectedReservation
    .warehouseId &&
  canReallocate && (

  <div className="inventory-details-section">

    <h3>
      إعادة توزيع الحجز
    </h3>


    <div className="inventory-reallocate-current">

      المستودع الحالي:

      <strong>
        {getWarehouseName(
          selectedReservation
            .warehouseId,
        )}
      </strong>

    </div>


    <div className="inventory-reallocate-form">

      <div className="inventory-reallocate-grid">

        <label>

          <span>
            المستودع الهدف
          </span>

          <select
            value={
              targetWarehouseId
            }
            onChange={(
              event,
            ) =>
              setTargetWarehouseId(
                event.target.value,
              )
            }
            disabled={
              reallocatingReservation ||
              releasingReservation
            }
          >

            <option value="">
              اختر المستودع
            </option>

            {warehouses
              .filter(
                warehouse =>
                  warehouse.id !==
                    selectedReservation
                      .warehouseId &&
                  warehouse.isActive,
              )
              .map(
                warehouse => (

                  <option
                    key={
                      warehouse.id
                    }
                    value={
                      warehouse.id
                    }
                  >

                    {warehouse.nameAr ||
                      warehouse.name}

                    {" — "}

                    {warehouse.code}

                  </option>

                ),
              )}

          </select>

        </label>


        <label>

          <span>
            الكمية
          </span>

          <input
            type="number"
            min="1"
            max={
              selectedReservation
                .quantity
            }
            step="1"
            value={
              reallocationQuantity
            }
            onChange={(
              event,
            ) =>
              setReallocationQuantity(
                event.target.value,
              )
            }
            placeholder={`كامل الحجز (${selectedReservation.quantity})`}
            disabled={
              reallocatingReservation ||
              releasingReservation
            }
          />

          <small>
            اتركها فارغة لنقل كامل الحجز.
          </small>

        </label>

      </div>


      <label>

        <span>
          سبب إعادة التوزيع
        </span>

        <textarea
          value={
            reallocationReason
          }
          onChange={(
            event,
          ) =>
            setReallocationReason(
              event.target.value,
            )
          }
          placeholder="مثال: تحويل تجهيز الطلب إلى مستودع بغداد"
          maxLength={
            500
          }
          disabled={
            reallocatingReservation ||
            releasingReservation
          }
        />

      </label>


      <div className="inventory-warehouse-feature-warning">
        ملاحظة: طالما Warehouse Inventory غير مفعّل في الـ Backend، ستُرفض العملية بأمان بدون أي تعديل على المخزون.
      </div>


      <div className="inventory-reallocate-actions">

        <button
          type="button"
          onClick={
            handleReallocateReservation
          }
          disabled={
            reallocatingReservation ||
            releasingReservation ||
            !targetWarehouseId ||
            !reallocationReason
              .trim()
          }
        >

          {reallocatingReservation
            ? "جاري إعادة التوزيع..."
            : "إعادة توزيع الحجز"}

        </button>

      </div>

    </div>

  </div>

)}

                {/* =========================================
                 * Release Active Reservation
                 * ======================================= */}

                {selectedReservation.status ===
                  "active" && (

                  <div className="inventory-details-section">

                    <h3>
                      تحرير الحجز
                    </h3>


                    <div className="inventory-release-warning">

                      سيتم إرجاع كمية
                      {" "}

                      <strong>
                        {selectedReservation.quantity}
                      </strong>

                      {" "}
                      إلى مخزون المنتج عند تحرير هذا الحجز.

                    </div>


                    <div className="inventory-release-form">

                      <label
                        htmlFor="inventory-release-reason"
                      >
                        سبب تحرير الحجز
                      </label>

                      <textarea
                        id="inventory-release-reason"
                        value={
                          releaseReason
                        }
                        onChange={(
                          event,
                        ) =>
                          setReleaseReason(
                            event.target.value,
                          )
                        }
                        placeholder="مثال: تم إلغاء الطلب أو لم يعد الحجز مطلوبًا"
                        rows={
                          4
                        }
                        maxLength={
                          500
                        }
                        disabled={
                          releasingReservation ||
                          reallocatingReservation
                        }
                      />


                      <div className="inventory-release-actions">

                        <button
                          type="button"
                          className="inventory-release-cancel-btn"
                          onClick={
                            handleCloseReservationDetails
                          }
                          disabled={
                            releasingReservation ||
                            reallocatingReservation
                          }
                        >
                          إلغاء
                        </button>


                        <button
                          type="button"
                          className="inventory-release-submit-btn"
                          onClick={
                            handleReleaseReservation
                          }
                          disabled={
                            releasingReservation ||
                            reallocatingReservation ||
                            !releaseReason.trim()
                          }
                        >

                          {releasingReservation
                            ? "جاري تحرير الحجز..."
                            : "تحرير الحجز وإرجاع المخزون"}

                        </button>

                      </div>

                    </div>

                  </div>

                )}

              </div>

            )}

          </Box>

        </Modal>

      </div>
    );
  };


export default Inventory;